import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Lista de prefijos de países permitidos (solo los de 2 cifras, sin +1 ni 3 cifras)
const countryPrefixes = [
  { prefix: '+34' }, // España (default)
  { prefix: '+33' }, // Francia
  { prefix: '+39' }, // Italia
  { prefix: '+49' }, // Alemania
  { prefix: '+44' }, // Reino Unido
  { prefix: '+54' }, // Argentina
  { prefix: '+56' }, // Chile
  { prefix: '+52' }, // México
  { prefix: '+57' }, // Colombia
  { prefix: '+51' }, // Perú
  { prefix: '+58' }, // Venezuela
  { prefix: '+55' }, // Brasil
];

// Función para limpiar el número de teléfono (quitar espacios, guiones, etc.)
const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/[\s\-\(\)]/g, '');
};

// Función para limpiar y formatear el número para guardarlo (solo dígitos, sin + ni espacios)
const cleanPhoneForDatabase = (phone: string): string => {
  return phone.replace(/[^\d]/g, '');
};

// Función para formatear el número de teléfono para mostrar en formato '+Prefijo 111 22 33 44'
const formatPhoneForDisplay = (phone: string): string => {
  if (!phone) return '';
  // Lista de prefijos permitidos (sin el '+') ordenados de mayor a menor longitud
  const allowedPrefixes = ['34','33','39','49','44','54','56','52','57','51','58','55'];
  // Buscar el prefijo real
  let prefix = '';
  let rest = phone;
  for (const p of allowedPrefixes) {
    if (phone.startsWith(p)) {
      prefix = p;
      rest = phone.slice(p.length);
      break;
    }
  }
  // Si no se detecta prefijo, usar los primeros 2 dígitos por defecto
  if (!prefix) {
    prefix = phone.slice(0, 2);
    rest = phone.slice(2);
  }
  // Limpiar el resto del número
  rest = rest.replace(/[^0-9]/g, '');
  // Agrupar en bloques: 3-2-2-2 (o lo que quede)
  let blocks = [];
  if (rest.length > 0) {
    blocks.push(rest.slice(0, 3));
    if (rest.length > 3) blocks.push(rest.slice(3, 5));
    if (rest.length > 5) blocks.push(rest.slice(5, 7));
    if (rest.length > 7) blocks.push(rest.slice(7, 9));
    if (rest.length > 9) blocks.push(rest.slice(9));
  }
  return `+${prefix} ${blocks.join(' ')}`.trim();
};

const formatUserNameAndEmail = (user: { name?: string, email: string }) => {
  if (!user.name || user.name.trim() === '') {
    return (
      <>
        <span className="text-red-500 font-medium">Usuario sin nombre</span>
        <span className=" mx-2 font-bold">·</span>
        <span className=" font-medium">{user.email}</span>
      </>
    );
  }
  
  return (
    <>
      <span className="font-bold">{user.name}</span>
      <span className="mx-2 font-bold">·</span>
      <span className=" font-medium">{user.email}</span>
    </>
  );
};

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  useOwnPhotos: boolean;
  grantDate?: string;
  clientType: string;
  plan?: string;
  phone: string;
  instagramId?: string;
  facebookId?: string;
  projectId?: string;
  // Añadir campo SEO opcional
  seo?: string;
  // Añadir campo associatedUserId para detectar usuarios multicuenta
  associatedUserId?: number | null;
  web?: string;
  // Añadir campo autoposting
  autoposting?: boolean;
  // Añadir campo confirmationMailAddress
  confirmationMailAddress?: string;
  // Añadir campo confirmationPhone
  confirmationPhone?: string;
}

// Estado para edición de usuario
interface EditForm {
  email: string;
  name: string;
  password: string;
  instagramId: string;
  facebookId: string;
  projectId: string;
  phone: string;
  plan: string;
  seo: string[];
  useOwnPhotos: boolean;
  web?: string;
  autoposting: boolean;
  confirmationMailAddress?: string;
  confirmationPhone?: string;
}

/** Plan SEO 2 publicaciones/mes en el formulario de registro (solo SEO o BOTH). */
function isRegisterSeo2mSelected(form: {
  role: string;
  planType: string;
  plan: string;
  seoPlan: string;
}): boolean {
  if (form.role === 'ADMIN') return false;
  if (form.planType === 'SEO') return form.plan === 'SEO_2M';
  if (form.planType === 'BOTH') return form.seoPlan === 'SEO_2M';
  return false;
}

const ADMIN_USERS_LIST_STATE_KEY = 'adminUsersListState';

interface AdminUsersListState {
  page: number;
  search: string;
  filterRRSS: boolean;
  filterSEO: boolean;
  filterAdmin: boolean;
}

const DEFAULT_ADMIN_USERS_LIST_STATE: AdminUsersListState = {
  page: 1,
  search: '',
  filterRRSS: true,
  filterSEO: true,
  filterAdmin: false,
};

function loadAdminUsersListState(): AdminUsersListState {
  try {
    const raw = sessionStorage.getItem(ADMIN_USERS_LIST_STATE_KEY);
    if (!raw) return DEFAULT_ADMIN_USERS_LIST_STATE;
    const parsed = JSON.parse(raw);
    const filterAdmin = typeof parsed.filterAdmin === 'boolean'
      ? parsed.filterAdmin
      : DEFAULT_ADMIN_USERS_LIST_STATE.filterAdmin;
    let filterRRSS = typeof parsed.filterRRSS === 'boolean'
      ? parsed.filterRRSS
      : DEFAULT_ADMIN_USERS_LIST_STATE.filterRRSS;
    let filterSEO = typeof parsed.filterSEO === 'boolean'
      ? parsed.filterSEO
      : DEFAULT_ADMIN_USERS_LIST_STATE.filterSEO;
    if (filterAdmin) {
      filterRRSS = false;
      filterSEO = false;
    } else if (!filterRRSS && !filterSEO) {
      filterRRSS = true;
      filterSEO = true;
    }
    return {
      page: Number.isInteger(parsed.page) && parsed.page >= 1 ? parsed.page : 1,
      search: typeof parsed.search === 'string' ? parsed.search : '',
      filterRRSS,
      filterSEO,
      filterAdmin,
    };
  } catch {
    return DEFAULT_ADMIN_USERS_LIST_STATE;
  }
}

const AdminUserListScreen: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [initialListState] = useState(loadAdminUsersListState);
  const [search, setSearch] = useState(initialListState.search);
  const navigate = useNavigate();
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const prevListFiltersRef = useRef({
    search: initialListState.search,
    filterRRSS: initialListState.filterRRSS,
    filterSEO: initialListState.filterSEO,
    filterAdmin: initialListState.filterAdmin,
  });
  const USERS_PER_PAGE = 10;
  const [page, setPage] = useState(initialListState.page);
  const [total, setTotal] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState<{
    name: string;
    email: string;
    password: string;
    role: string;
    planType: string;
    plan: string;
    useOwnPhotos: boolean;
    instagramId: string;
    facebookId: string;
    projectId: string;
    seoProjectId: string; // Nuevo campo para Project ID de SEO
    phone: string;
    phonePrefix: string;
    selectedPlans: string[];
    seoPlan: string;
    rrssPlan: string;
    seo: string[];
    web?: string;
    autoposting: boolean;
    confirmationMailAddress?: string;
    confirmationPhone?: string;
    confirmationPhonePrefix: string;
  }>(
    {
      name: '',
      email: '',
      password: '',
      role: 'USER',
      planType: 'RRSS',
      plan: 'RRSS_4M',
      useOwnPhotos: false,
      instagramId: '',
      facebookId: '',
      projectId: '',
      seoProjectId: '', // Nuevo campo para Project ID de SEO
      phone: '',
      phonePrefix: '+34',
      selectedPlans: [],
      seoPlan: 'SEO_2M',
      rrssPlan: 'RRSS_4M',
      seo: [],
      web: '',
      autoposting: false,
      confirmationMailAddress: '',
      confirmationPhone: '',
      confirmationPhonePrefix: '+34',
    }
  );
  const [registerAutopostingManual, setRegisterAutopostingManual] = useState(false);
  const lastAutopostingSuggestKeyRef = useRef<string | null>(null);
  const [registerError, setRegisterError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  
  // Estado para controlar si la confirmación de posts está activada en registro
  const [registerConfirmationEnabled, setRegisterConfirmationEnabled] = useState<boolean>(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [filterRRSS, setFilterRRSS] = useState(initialListState.filterRRSS);
  const [filterSEO, setFilterSEO] = useState(initialListState.filterSEO);
  const [filterAdmin, setFilterAdmin] = useState(initialListState.filterAdmin);
  const [updatingUsers, setUpdatingUsers] = useState<Set<number>>(new Set());
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isFadingOut, setIsFadingOut] = useState(false);
  // Estado para el modo de eliminación
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [usersToDelete, setUsersToDelete] = useState<Set<number>>(new Set());
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [allUsersForDelete, setAllUsersForDelete] = useState<User[]>([]);
  // Estado para edición de usuario
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    email: '',
    name: '',
    password: '',
    instagramId: '',
    facebookId: '',
    projectId: '',
    phone: '',
    plan: '',
    seo: [],
    useOwnPhotos: false,
    web: '',
    autoposting: true,
    confirmationMailAddress: '',
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  // Estado para prefijo de teléfono en edición
  const [editPhonePrefix, setEditPhonePrefix] = useState<string>(editForm.phone?.startsWith('34') ? '+34' : '+34');
  // Estado para prefijo de confirmationPhone en edición
  const [editConfirmationPhonePrefix, setEditConfirmationPhonePrefix] = useState<string>('+34');
  
  // Estado para controlar si la confirmación de posts está activada
  const [confirmationEnabled, setConfirmationEnabled] = useState<boolean>(false);
  
  // Estados para la funcionalidad de añadir plan
  const [showAddPlanSection, setShowAddPlanSection] = useState<number | null>(null);
  const [newPlanToAdd, setNewPlanToAdd] = useState<string>('');
  const [addPlanLoading, setAddPlanLoading] = useState(false);
  const [addPlanError, setAddPlanError] = useState('');
  
  // Estados para los campos específicos del plan a añadir
  const [newPlanFields, setNewPlanFields] = useState<{
    seoType: string;
    seoProjectId: string;
    rrssInstagramId: string;
    rrssFacebookId: string;
    rrssProjectId: string;
  }>({
    seoType: 'BLOG',
    seoProjectId: '',
    rrssInstagramId: '',
    rrssFacebookId: '',
    rrssProjectId: '',
  });

  // Estados para el desplegable de envío de correo
  const [emailingUserId, setEmailingUserId] = useState<number | null>(null);
  const [emailDestination, setEmailDestination] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // Estado para email alternativo en el registro
  const [showAltEmail, setShowAltEmail] = useState(false);
  const [altEmail, setAltEmail] = useState('');

  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 100);
    return () => setIsLoaded(false);
  }, []);

  // Cerrar calendario al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendar(false);
      }
    }
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', String(USERS_PER_PAGE));
      if (search) params.append('nameSearch', search);
      if (filterSEO) {
        params.append('planFilter', 'SEO');
      }
      if (filterRRSS) {
        params.append('planFilter', 'RRSS');
      }
      if (filterAdmin) {
        params.append('roleFilter', 'ADMIN');
      }
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setTotal(data.total);
      } else {
        setError('No autorizado o error al cargar usuarios.');
      }
    } catch (err) {
      setError('Error de red.');
    }
    setLoading(false);
  };

  // Función para obtener todos los usuarios (para el mensaje de confirmación)
  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('limit', '1000'); // Obtener todos los usuarios
      if (search) params.append('nameSearch', search);
      if (filterSEO) {
        params.append('planFilter', 'SEO');
      }
      if (filterRRSS) {
        params.append('planFilter', 'RRSS');
      }
      if (filterAdmin) {
        params.append('roleFilter', 'ADMIN');
      }
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllUsersForDelete(data.users);
      }
    } catch (err) {
      console.error('Error al obtener todos los usuarios:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [page, search, filterRRSS, filterSEO, filterAdmin]);

  useEffect(() => {
    sessionStorage.setItem(
      ADMIN_USERS_LIST_STATE_KEY,
      JSON.stringify({ page, search, filterRRSS, filterSEO, filterAdmin })
    );
  }, [page, search, filterRRSS, filterSEO, filterAdmin]);

  const handleSelect = (user: User) => {
    setIsExiting(true);
    setTimeout(() => {
      navigate('/calendar', { state: { adminViewUserId: user.id, adminViewUserEmail: user.email, adminViewUserPlan: user.plan } });
    }, 350);
  };

  const totalPages = Math.ceil(total / USERS_PER_PAGE);
  const startIdx = (page - 1) * USERS_PER_PAGE;
  const endIdx = Math.min(startIdx + USERS_PER_PAGE, total);
  const paginatedUsers = users;

  // Si la búsqueda o los filtros cambian, vuelve a la página 1
  useEffect(() => {
    const prev = prevListFiltersRef.current;
    const filtersChanged =
      prev.search !== search ||
      prev.filterRRSS !== filterRRSS ||
      prev.filterSEO !== filterSEO ||
      prev.filterAdmin !== filterAdmin;
    prevListFiltersRef.current = { search, filterRRSS, filterSEO, filterAdmin };
    if (filtersChanged) {
      setPage(1);
    }
  }, [search, filterRRSS, filterSEO, filterAdmin]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  // Función para mostrar popup de éxito
  const showSuccessNotification = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessPopup(true);
    setIsFadingOut(false);
    
    // Scroll instantáneo hacia arriba para mostrar el popup
    window.scrollTo({ top: 0, behavior: 'auto' });
    
    setTimeout(() => {
      setIsFadingOut(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
        setIsFadingOut(false);
      }, 300); // Tiempo para la animación de fade out
    }, 2200); // Inicia fade out después de 2.2 segundos
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevenir múltiples envíos
    if (registerLoading) {
      return;
    }
    
    setRegisterError('');
    
    // Validación para Project ID
    if (registerForm.role !== 'ADMIN') {
      if (registerForm.planType === 'BOTH') {
        // Para BOTH, validar ambos Project ID
        if (!registerForm.projectId.trim()) {
          setRegisterError('El Project ID de Redes Sociales es obligatorio.');
          return;
        }
        if (!registerForm.seoProjectId.trim()) {
          setRegisterError('El Project ID de SEO es obligatorio.');
          return;
        }
      } else {
        // Para RRSS o SEO, validar solo el Project ID principal
        if (!registerForm.projectId.trim()) {
          setRegisterError('El Project ID es obligatorio para usuarios no-admin.');
          return;
        }
      }
    }
    
    // Validación para teléfono (obligatorio para RRSS, SEO y BOTH)
    if (registerForm.role !== 'ADMIN' && (registerForm.planType === 'RRSS' || registerForm.planType === 'SEO' || registerForm.planType === 'BOTH') && !registerForm.phone.trim()) {
      setRegisterError('El campo Teléfono es obligatorio para usuarios de Redes sociales y SEO.');
      return;
    }
    
    // Validación para redes sociales (al menos una obligatoria para RRSS y BOTH)
    if (registerForm.role !== 'ADMIN' && (registerForm.planType === 'RRSS' || registerForm.planType === 'BOTH')) {
      const hasInstagram = registerForm.instagramId && registerForm.instagramId.trim();
      const hasFacebook = registerForm.facebookId && registerForm.facebookId.trim();
      
      if (!hasInstagram && !hasFacebook) {
        setRegisterError('Debes proporcionar al menos un Instagram ID o Facebook ID para usuarios de Redes sociales.');
        return;
      }
    }
    
    // En handleRegister, validar el campo seo si el plan es SEO o BOTH
    if ((registerForm.planType === 'SEO' || registerForm.planType === 'BOTH') && (!registerForm.seo || registerForm.seo.length === 0)) {
      setRegisterError('Debes seleccionar al menos un tipo de SEO (Blog o Bloque de texto).');
      return;
    }
    
    // Determinar qué endpoint usar y preparar los datos
    const isMultiPlan = registerForm.planType === 'BOTH';
    const endpoint = isMultiPlan ? '/register-multi-plan' : '/register';
    
    // Procesar el número de teléfono: combinar prefijo + número sin formato
    let processedPhone = '';
    if (registerForm.phone.trim()) {
      const cleanedPhone = cleanPhoneNumber(registerForm.phone);
      const prefixWithoutPlus = registerForm.phonePrefix.replace('+', '');
      processedPhone = prefixWithoutPlus + cleanedPhone;
    }
    
    // Determinar email de envío
    const sendToEmail = altEmail.trim() !== '' ? altEmail.trim() : registerForm.email.trim();
    
    setRegisterLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { planType, phonePrefix, ...formToSend } = registerForm;
      if (formToSend.role === 'ADMIN' && 'plan' in formToSend) {
        delete (formToSend as any).plan;
      }
      // Eliminar instagramId y facebookId si no es RRSS ni BOTH
      if (planType !== 'RRSS' && planType !== 'BOTH') {
        delete (formToSend as any).instagramId;
        delete (formToSend as any).facebookId;
      } else {
        if (formToSend.instagramId) {
          (formToSend as any).instagram_id = formToSend.instagramId;
          delete (formToSend as any).instagramId;
        }
        if (formToSend.facebookId) {
          (formToSend as any).facebook_id = formToSend.facebookId;
          delete (formToSend as any).facebookId;
        }
      }
      // Manejar Project ID según el tipo de plan
      if (planType === 'BOTH') {
        const projectIds = [];
        for (const plan of registerForm.selectedPlans) {
          if (plan.startsWith('SEO')) {
            projectIds.push(registerForm.seoProjectId);
          } else if (plan.startsWith('RRSS')) {
            projectIds.push(registerForm.projectId);
          }
        }
        (formToSend as any).project_ids = projectIds;
        delete (formToSend as any).projectId;
        delete (formToSend as any).seoProjectId;
      } else {
        if (formToSend.projectId) {
          (formToSend as any).project_id = formToSend.projectId;
          delete (formToSend as any).projectId;
        }
        delete (formToSend as any).seoProjectId;
      }
      // Usar el teléfono procesado
      if (processedPhone) {
        (formToSend as any).phone = processedPhone;
      }

      // Preparar datos según el tipo de registro
      let requestData;
      if (isMultiPlan) {
        requestData = {
          ...formToSend,
          plans: registerForm.selectedPlans
        };
      } else {
        requestData = formToSend;
      }

      // Añadir campo seo si corresponde
      if (planType === 'SEO' || planType === 'BOTH') {
        if (isMultiPlan) {
          const seoPlans = registerForm.selectedPlans.filter(p => p.startsWith('SEO'));
          if (seoPlans.length > 1) {
            requestData.seo = seoPlans.map(() => (Array.isArray(registerForm.seo) ? registerForm.seo[0] : (registerForm.seo as any)));
          } else {
            requestData.seo = Array.isArray(registerForm.seo) ? registerForm.seo[0] : (registerForm.seo as any);
          }
        } else {
          requestData.seo = Array.isArray(registerForm.seo) ? registerForm.seo[0] : (registerForm.seo as any);
        }
      }

      // Añadir el campo sendToEmail si es diferente al email principal
      if (sendToEmail !== registerForm.email.trim()) {
        (requestData as any).sendToEmail = sendToEmail;
      }

      // Añadir el campo web solo si no es ADMIN
      if (registerForm.role !== 'ADMIN' && registerForm.web) {
        (requestData as any).web = registerForm.web;
      }

      // Añadir el campo autoposting
      (requestData as any).autoposting = registerForm.autoposting;
      
      // Añadir los campos de confirmación solo para usuarios RRSS
      if (registerForm.role !== 'ADMIN' && (registerForm.planType === 'RRSS' || registerForm.planType === 'BOTH')) {
        if (registerConfirmationEnabled) {
          if (registerForm.confirmationMailAddress) {
            (requestData as any).confirmationMailAddress = registerForm.confirmationMailAddress;
          }
          if (registerForm.confirmationPhone) {
            // Limpiar el confirmationPhone: prefijo + número sin espacios ni símbolos
            const cleanedConfirmationPhone = cleanPhoneForDatabase(registerForm.confirmationPhonePrefix + registerForm.confirmationPhone);
            (requestData as any).confirmationPhone = cleanedConfirmationPhone;
          }
        }
      }

      // LOG para depuración
      console.log('🔍 Registro usuario - endpoint:', isMultiPlan ? '/register-multi-plan' : '/register');
      console.log('🔍 Registro usuario - registerConfirmationEnabled:', registerConfirmationEnabled);
      console.log('🔍 Registro usuario - confirmationMailAddress:', registerForm.confirmationMailAddress);
      console.log('🔍 Registro usuario - confirmationPhone:', registerForm.confirmationPhone);
      console.log('🔍 Registro usuario - requestData:', requestData);
      console.log('🔍 Registro usuario - valor final de seo:', requestData.seo);
      const res = await fetch(`${process.env.REACT_APP_API_URL}${isMultiPlan ? '/register-multi-plan' : '/register'}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      if (res.ok) {
        const data = await res.json();
        setShowRegisterModal(false);
        setRegisterForm({
          name: '',
          email: '',
          password: '',
          role: 'USER',
          planType: 'RRSS',
          plan: 'RRSS_4M',
          useOwnPhotos: false,
          instagramId: '',
          facebookId: '',
          projectId: '',
          seoProjectId: '',
          phone: '',
          phonePrefix: '+34',
          selectedPlans: [],
          seoPlan: 'SEO_2M',
          rrssPlan: 'RRSS_4M',
          seo: [],
          web: '',
          autoposting: false,
          confirmationMailAddress: '',
          confirmationPhone: '',
          confirmationPhonePrefix: '+34',
        });
        setRegisterAutopostingManual(false);
        setRegisterConfirmationEnabled(false);
        setAltEmail('');
        setShowAltEmail(false);
        // Mostrar popup de éxito
        if (isMultiPlan) {
          showSuccessNotification(`¡Éxito! Se han creado ${data.users?.length || 2} cuentas para el cliente con múltiples planes.`);
        } else {
          showSuccessNotification('Usuario registrado exitosamente.');
        }
        fetchUsers();
      } else {
        const data = await res.json();
        setRegisterError(data.error || 'Error al registrar usuario');
      }
    } catch (err) {
      setRegisterError('Error de red');
    }
    setRegisterLoading(false);
  };

  // Función para extraer el número de publicaciones del plan
  const getPublicationsCount = (plan: string | undefined) => {
    if (!plan) return null;
    
    if (plan.includes('_2M')) return '2';
    if (plan.includes('_4M')) return '4';
    if (plan.includes('_6M')) return '6';
    if (plan.includes('_12M')) return '12';
    
    return null;
  };

  // Función para cambiar el tipo de imágenes de un usuario
  const handleToggleUseOwnPhotos = async (userId: number, newValue: boolean) => {
    if (updatingUsers.has(userId)) return; // Evitar múltiples clicks
    
    setUpdatingUsers(prev => new Set(prev).add(userId));
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}/use-own-photos`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ useOwnPhotos: newValue }),
      });
      
      if (res.ok) {
        // Actualizar la lista de usuarios localmente
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, useOwnPhotos: newValue }
              : user
          )
        );
      } else {
        const data = await res.json();
        console.error('Error al actualizar usuario:', data.error);
        // Aquí podrías mostrar un toast o notificación de error
      }
    } catch (err) {
      console.error('Error de red al actualizar usuario:', err);
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Función para obtener el tipo de plan legible
  const getPlanType = (plan?: string) => {
    if (!plan) return '';
    if (plan.startsWith('SEO')) return 'SEO';
    if (plan.startsWith('RRSS')) return 'Redes Sociales';
    return plan;
  };



  // Funciones para el modo de eliminación en la lista principal
  const toggleDeleteMode = () => {
    if (isDeleteMode) {
      setIsDeleteMode(false);
      setUsersToDelete(new Set());
      setAllUsersForDelete([]);
    } else {
      // Cerrar todos los formularios abiertos
      setEditingUserId(null);
      setEmailingUserId(null);
      setEmailDestination('');
      setNewPassword('');
      setEmailLoading(false);
      setShowRegisterModal(false);
      
      // Resetear formularios
      setEditForm({
        email: '',
        name: '',
        password: '',
        instagramId: '',
        facebookId: '',
        projectId: '',
        phone: '',
        plan: '',
        seo: [],
        useOwnPhotos: false,
        web: '',
        autoposting: true,
        confirmationMailAddress: '',
        confirmationPhone: '',
      });
      setEditError('');
      
      setIsDeleteMode(true);
      fetchAllUsers(); // Obtener todos los usuarios para el mensaje de confirmación
    }
  };

  const handleUserSelectForDelete = (userId: number) => {
    if (!isDeleteMode) return;
    setUsersToDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleConfirmDelete = async () => {
    if (usersToDelete.size === 0) return;
    setDeleteConfirmLoading(true);
    try {
      const token = localStorage.getItem('token');
      for (const userId of Array.from(usersToDelete)) {
        await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      const deletedCount = usersToDelete.size;
      setUsersToDelete(new Set());
      setIsDeleteMode(false);
      setAllUsersForDelete([]);
      showSuccessNotification(`${deletedCount} usuarios eliminados correctamente.`);
      fetchUsers();
    } catch (err) {
      alert('Error al eliminar usuarios.');
    }
    setDeleteConfirmLoading(false);
  };

  const handleCancelDelete = () => {
    setUsersToDelete(new Set());
    setIsDeleteMode(false);
    setAllUsersForDelete([]);
  };

  // Función para abrir/cerrar el formulario de edición
  const handleEdit = (user: User) => {
    // Si ya está editando este usuario, cerrar
    if (editingUserId === user.id) {
      setEditingUserId(null);
      setEditForm({
        email: '',
        name: '',
        password: '',
        instagramId: '',
        facebookId: '',
        projectId: '',
        phone: '',
        plan: '',
        seo: [],
        useOwnPhotos: false,
        web: '',
        autoposting: true,
        confirmationMailAddress: '',
        confirmationPhone: '',
      });
      setEditError('');
    } else {
      // Cerrar cualquier otro desplegable abierto
      setEmailingUserId(null);
      setEmailDestination('');
      setNewPassword('');
      setEmailLoading(false);
      
      // Cerrar formulario de registro
      setShowRegisterModal(false);
      
      // Cerrar modo de eliminación
      setIsDeleteMode(false);
      setUsersToDelete(new Set());
      setAllUsersForDelete([]);
      
      // Extraer el prefijo del confirmationPhone si existe
      let confirmationPhoneWithoutPrefix = '';
      let confirmationPrefix = '+34';
      if (user.confirmationPhone) {
        const allowedPrefixes = ['34','33','39','49','44','54','56','52','57','51','58','55'];
        let foundPrefix = false;
        for (const p of allowedPrefixes) {
          if (user.confirmationPhone.startsWith(p)) {
            confirmationPrefix = '+' + p;
            confirmationPhoneWithoutPrefix = user.confirmationPhone.slice(p.length);
            foundPrefix = true;
            break;
          }
        }
        if (!foundPrefix) {
          // Si no se encuentra prefijo, asumir que los primeros 2 dígitos son el prefijo
          confirmationPrefix = '+' + user.confirmationPhone.slice(0, 2);
          confirmationPhoneWithoutPrefix = user.confirmationPhone.slice(2);
        }
      }
      setEditConfirmationPhonePrefix(confirmationPrefix);
      
      // Abrir el formulario de edición
      setEditingUserId(user.id);
      setEditForm({
        email: user.email,
        name: user.name || '',
        password: '',
        instagramId: user['instagramId'] || '',
        facebookId: user['facebookId'] || '',
        projectId: user['projectId'] || '',
        phone: user.phone || '',
        plan: user.plan || '',
        // Siempre pasar el valor recibido de user.seo a array de string si es válido
        seo: (user.seo === 'BLOG' || user.seo === 'BLOQUE_TEXTO') ? [user.seo] : [],
        useOwnPhotos: user.useOwnPhotos,
        web: user.web || '',
        autoposting: user.autoposting !== undefined ? user.autoposting : true,
        confirmationMailAddress: user.confirmationMailAddress || '',
        confirmationPhone: confirmationPhoneWithoutPrefix,
      });
      // Inicializar el estado de confirmación basado en si ya tiene email o teléfono de confirmación
      setConfirmationEnabled(!!(user.confirmationMailAddress || user.confirmationPhone));
      setEditError('');
    }
  };

  // Función para cancelar edición
  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditForm({
      email: '',
      name: '',
      password: '',
      instagramId: '',
      facebookId: '',
      projectId: '',
      phone: '',
      plan: '',
      seo: [],
      useOwnPhotos: false,
      web: '',
      autoposting: true,
      confirmationMailAddress: '',
    });
    setConfirmationEnabled(false);
    setEditError('');
  };

  // Función para guardar cambios
  const handleSaveEdit = async (userId: number) => {
    setEditLoading(true);
    setEditError('');
    try {
      const token = localStorage.getItem('token');
      let body: any = { ...editForm };
      if (!body.password) delete body.password;
      // Obtener el usuario actual para saber su rol
      const currentUser = users.find(u => u.id === userId);
      // Si el plan es SEO, enviar seo como string (no array) y NO enviar RRSS
      if (body.plan && body.plan.startsWith('SEO')) {
        delete body.facebookId;
        delete body.instagramId;
        body.seo = Array.isArray(editForm.seo) ? editForm.seo[0] : editForm.seo;
      }
      // Si el plan es RRSS, NO enviar el campo seo para evitar que se establezca como null
      else if (body.plan && body.plan.startsWith('RRSS')) {
        delete body.seo;
        // Solo para usuarios RRSS, manejar campos de confirmación
        if (confirmationEnabled) {
          if (editForm.confirmationMailAddress) {
            body.confirmationMailAddress = editForm.confirmationMailAddress;
          } else {
            body.confirmationMailAddress = null;
          }
          if (editForm.confirmationPhone) {
            // Limpiar el confirmationPhone: prefijo + número sin espacios ni símbolos
            const cleanedConfirmationPhone = cleanPhoneForDatabase(editConfirmationPhonePrefix + editForm.confirmationPhone);
            body.confirmationPhone = cleanedConfirmationPhone;
          } else {
            body.confirmationPhone = null;
          }
        } else {
          // Si está desactivado, enviar null para limpiar ambos campos
          body.confirmationMailAddress = null;
          body.confirmationPhone = null;
        }
      } else {
        // Para usuarios no-RRSS (SEO, ADMIN), no enviar confirmationMailAddress ni confirmationPhone
        delete body.confirmationMailAddress;
        delete body.confirmationPhone;
      }
      // FORZAR el envío de useOwnPhotos como booleano explícito
      body.useOwnPhotos = Boolean(editForm.useOwnPhotos);
      // FORZAR el envío de autoposting como booleano explícito
      body.autoposting = Boolean(editForm.autoposting);
      // Añadir el campo web solo si no es ADMIN
      if (currentUser && currentUser.role !== 'ADMIN' && editForm.web) {
        body.web = editForm.web;
      }
      
      // LOG para depuración
      console.log('🔍 Editar usuario - userId:', userId);
      console.log('🔍 Editar usuario - confirmationEnabled:', confirmationEnabled);
      console.log('🔍 Editar usuario - confirmationMailAddress:', editForm.confirmationMailAddress);
      console.log('🔍 Editar usuario - confirmationPhone:', editForm.confirmationPhone);
      console.log('🔍 Editar usuario - body completo:', body);
      
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        showSuccessNotification('Usuario actualizado correctamente.');
        setEditingUserId(null);
        setEditForm({
          email: '',
          name: '',
          password: '',
          instagramId: '',
          facebookId: '',
          projectId: '',
          phone: '',
          plan: '',
          seo: [],
          useOwnPhotos: false,
          web: '',
          autoposting: true,
          confirmationMailAddress: '',
        });
        setConfirmationEnabled(false);
        fetchUsers();
      } else {
        const data = await res.json();
        setEditError(data.error || 'Error al actualizar usuario');
      }
    } catch (err) {
      setEditError('Error de red');
    }
    setEditLoading(false);
  };

  useEffect(() => {
    if (registerForm.planType === 'SEO' || registerForm.planType === 'BOTH') {
      if (!registerForm.seo || registerForm.seo.length === 0 || (registerForm.seo[0] !== 'BLOG' && registerForm.seo[0] !== 'BLOQUE_TEXTO')) {
        setRegisterForm(f => ({ ...f, seo: ['BLOG'] }));
      }
    } else {
      // Si el plan no es SEO ni BOTH, limpiar el campo seo
      if (registerForm.seo.length > 0) {
        setRegisterForm(f => ({ ...f, seo: [] }));
      }
    }
  }, [registerForm.planType]);

  useEffect(() => {
    if (!showRegisterModal) {
      lastAutopostingSuggestKeyRef.current = null;
      setRegisterAutopostingManual(false);
    }
  }, [showRegisterModal]);

  // Sugerencia de autoposting: activada solo para SEO 2 pub/mes; el admin puede cambiarla manualmente
  useEffect(() => {
    if (!showRegisterModal || registerForm.role !== 'USER') return;

    const planSelectionKey = `${registerForm.planType}|${registerForm.plan}|${registerForm.seoPlan}`;
    const planSelectionChanged =
      lastAutopostingSuggestKeyRef.current !== null &&
      lastAutopostingSuggestKeyRef.current !== planSelectionKey;

    if (planSelectionChanged) {
      setRegisterAutopostingManual(false);
    }
    lastAutopostingSuggestKeyRef.current = planSelectionKey;

    const respectManualChoice = !planSelectionChanged && registerAutopostingManual;
    if (respectManualChoice) return;

    const suggestedAutoposting = isRegisterSeo2mSelected(registerForm);
    setRegisterForm(f =>
      f.autoposting === suggestedAutoposting ? f : { ...f, autoposting: suggestedAutoposting }
    );
  }, [
    showRegisterModal,
    registerForm.role,
    registerForm.planType,
    registerForm.plan,
    registerForm.seoPlan,
    registerAutopostingManual,
  ]);

  // Función para detectar si un usuario tiene múltiples planes
  const hasMultiplePlans = (user: User): boolean => {
    // Un usuario tiene múltiples planes si:
    // 1. Tiene associatedUserId (es un usuario secundario)
    // 2. O tiene otros usuarios asociados a él (es el usuario principal)
    
    // Si el usuario tiene associatedUserId, significa que es parte de un grupo multicuenta
    if (user.associatedUserId) {
      return true;
    }
    
    // Si el usuario no tiene associatedUserId, verificar si tiene usuarios asociados a él
    const usersAssociatedToThis = users.filter(u => 
      u.associatedUserId === user.id
    );
    
    return usersAssociatedToThis.length > 0;
  };

  // Función para abrir/cerrar la sección de añadir plan
  const handleToggleAddPlanSection = (userId: number) => {
    if (showAddPlanSection === userId) {
      setShowAddPlanSection(null);
      setNewPlanToAdd('');
      setAddPlanError('');
      // Resetear campos específicos del plan
      setNewPlanFields({
        seoType: 'BLOG',
        seoProjectId: '',
        rrssInstagramId: '',
        rrssFacebookId: '',
        rrssProjectId: '',
      });
    } else {
      // Solo cerrar otros formularios que NO sean el de edición actual
      // (ya que la sección de añadir plan está contenida dentro del formulario de edición)
      setEmailingUserId(null);
      setEmailDestination('');
      setNewPassword('');
      setEmailLoading(false);
      setShowRegisterModal(false);
      
      // NO cerrar el formulario de edición (setEditingUserId(null))
      // NO resetear el formulario de edición
      
      // Cerrar modo de eliminación
      setIsDeleteMode(false);
      setUsersToDelete(new Set());
      setAllUsersForDelete([]);
      
      setShowAddPlanSection(userId);
      setNewPlanToAdd('');
      setAddPlanError('');
      // Resetear campos específicos del plan
      setNewPlanFields({
        seoType: 'BLOG',
        seoProjectId: '',
        rrssInstagramId: '',
        rrssFacebookId: '',
        rrssProjectId: '',
      });
    }
  };

  // Función para añadir un nuevo plan a un usuario existente
  // Funciones para el desplegable de envío de correo
  const handleSendEmail = (user: User) => {
    // Si ya está enviando email a este usuario, cerrar
    if (emailingUserId === user.id) {
      setEmailingUserId(null);
      setEmailDestination('');
      setNewPassword('');
      setEmailLoading(false);
    } else {
      // Cerrar cualquier otro desplegable abierto
      setEditingUserId(null);
      setEditForm({
        email: '',
        name: '',
        password: '',
        instagramId: '',
        facebookId: '',
        projectId: '',
        phone: '',
        plan: '',
        seo: [],
        useOwnPhotos: false,
        web: '',
        autoposting: true,
        confirmationMailAddress: '',
        confirmationPhone: '',
      });
      setEditError('');
      
      // Cerrar formulario de registro
      setShowRegisterModal(false);
      
      // Cerrar modo de eliminación
      setIsDeleteMode(false);
      setUsersToDelete(new Set());
      setAllUsersForDelete([]);
      
      // Abrir el formulario de email
      setEmailingUserId(user.id);
      setEmailDestination(user.email); // Pre-llenar con el email del usuario
      setNewPassword(''); // Limpiar contraseña
    }
  };

  const handleCancelEmail = () => {
    setEmailingUserId(null);
    setEmailDestination('');
    setNewPassword('');
    setEmailLoading(false);
  };

  const handleSendEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailDestination.trim() || !newPassword.trim()) return;
    
    setEmailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const user = users.find(u => u.id === emailingUserId);
      if (!user) {
        throw new Error('Usuario no encontrado');
      }

      // Buscar todos los ids asociados si es multiplan
      let userIdsToUpdate = [user.id];
      if (user.associatedUserId) {
        // Es usuario secundario, buscar el principal y todos los secundarios
        userIdsToUpdate = [user.associatedUserId, ...users.filter(u => u.associatedUserId === user.associatedUserId).map(u => u.id)];
      } else {
        // Es usuario principal, buscar todos los secundarios
        const secundarios = users.filter(u => u.associatedUserId === user.id).map(u => u.id);
        if (secundarios.length > 0) {
          userIdsToUpdate = [user.id, ...secundarios];
        }
      }

      // Eliminar duplicados por si acaso
      userIdsToUpdate = Array.from(new Set(userIdsToUpdate));

      // Hacer la petición para cada usuario
      let allOk = true;
      for (const uid of userIdsToUpdate) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/send-credentials`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: uid,
            destinationEmail: emailDestination,
            newPassword: newPassword
          }),
        });
        if (!response.ok) {
          allOk = false;
        }
      }

      if (allOk) {
        showSuccessNotification('Credenciales enviadas exitosamente');
        handleCancelEmail();
      } else {
        showSuccessNotification('Error al enviar credenciales a uno o más usuarios multiplan');
      }
    } catch (error) {
      console.error('Error:', error);
      showSuccessNotification(error instanceof Error ? error.message : 'Error al enviar credenciales');
    }
    setEmailLoading(false);
  };

  const handleAddPlan = async (user: User) => {
    if (!newPlanToAdd) {
      setAddPlanError('Por favor selecciona un plan');
      return;
    }

    // Validar campos específicos según el plan
    if (newPlanToAdd.startsWith('SEO')) {
      if (!newPlanFields.seoProjectId.trim()) {
        setAddPlanError('Por favor introduce el Project ID para SEO');
        return;
      }
    } else if (newPlanToAdd.startsWith('RRSS')) {
      if (!newPlanFields.rrssProjectId.trim()) {
        setAddPlanError('Por favor introduce el Project ID para RRSS');
        return;
      }
      if (!newPlanFields.rrssInstagramId.trim() && !newPlanFields.rrssFacebookId.trim()) {
        setAddPlanError('Por favor introduce al menos un ID de red social (Instagram o Facebook)');
        return;
      }
    }

    setAddPlanLoading(true);
    setAddPlanError('');

    try {
      const token = localStorage.getItem('token');
      // Preparar el body según el plan
      let body: any = {
        plan: newPlanToAdd,
        useOwnPhotos: user.useOwnPhotos,
        phone: user.phone
      };
      if (newPlanToAdd.startsWith('SEO')) {
        body.projectId = newPlanFields.seoProjectId;
        body.seo = newPlanFields.seoType;
      } else if (newPlanToAdd.startsWith('RRSS')) {
        body.projectId = newPlanFields.rrssProjectId;
        body.instagramId = newPlanFields.rrssInstagramId;
        body.facebookId = newPlanFields.rrssFacebookId;
      }
      // Llamar al nuevo endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${user.id}/add-plan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        showSuccessNotification(`Plan ${newPlanToAdd} añadido correctamente al usuario ${user.name}`);
        setShowAddPlanSection(null);
        setNewPlanToAdd('');
        // Resetear campos específicos
        setNewPlanFields({
          seoType: 'BLOG',
          seoProjectId: '',
          rrssInstagramId: '',
          rrssFacebookId: '',
          rrssProjectId: '',
        });
        fetchUsers(); // Recargar la lista de usuarios
      } else {
        const data = await response.json();
        setAddPlanError(data.error || 'Error al añadir el plan');
      }
    } catch (error) {
      setAddPlanError('Error de conexión');
    } finally {
      setAddPlanLoading(false);
    }
  };

  // Nueva función centralizada para cambiar filtros
  const handleFilterChange = (type: 'RRSS' | 'SEO' | 'ADMIN') => {
    if (type === 'ADMIN') {
      setFilterAdmin(true);
      setFilterSEO(false);
      setFilterRRSS(false);
    } else if (type === 'SEO') {
      setFilterSEO(v => {
        const newValue = !v;
        if (newValue) setFilterAdmin(false);
        // Si ambos quedan desactivados, reactivo RRSS por defecto
        if (!newValue && !filterRRSS) setFilterRRSS(true);
        return newValue;
      });
    } else if (type === 'RRSS') {
      setFilterRRSS(v => {
        const newValue = !v;
        if (newValue) setFilterAdmin(false);
        // Si ambos quedan desactivados, reactivo SEO por defecto
        if (!newValue && !filterSEO) setFilterSEO(true);
        return newValue;
      });
    }
  };




  return (
    <div
      className="bg-[#F8F9FA] min-h-screen w-full font-futura px-2 sm:px-8 pb-12 flex flex-col items-center transition-all duration-500 ease-in-out relative"
      style={{
        opacity: isExiting ? 0 : isLoaded ? 1 : 0,
        transform: isExiting ? 'translateY(-32px)' : isLoaded ? 'translateY(0)' : 'translateY(32px)'
      }}
    >
      {/* Botón Cerrar sesión */}
      <div className="absolute top-6 right-10 z-30">
        <button
          onClick={() => {
            setIsExiting(true);
            setTimeout(() => {
              navigate('/thank-you');
            }, 350);
          }}
          className="px-8 py-3 rounded-full bg-white shadow text-[#44aaf6] font-bold tracking-wide text-lg border border-white hover:bg-blue-50 transition-all"
          style={{ letterSpacing: '0.08em' }}
        >
          Cerrar sesión
        </button>
      </div>

      {/* Popup de éxito */}
      {showSuccessPopup && (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out ${
          isFadingOut ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}>
          <div className="bg-green-500 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}
      {/* Header con título y texto a la derecha */}
      <div className="w-full max-w-5xl mt-12 mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-4xl sm:text-5xl font-bold text-[#5AB2F7] text-left leading-tight mb-4 sm:mb-0">
          Ayúdanos a crear<br />tu contenido.
        </h1>
        <div className="sm:ml-12 max-w-xl">
          <h2 className="text-3xl font-bold text-black mb-2">Elige la fecha de tu publicación</h2>
          <p className="text-sm text-black mb-1">Selecciona el día en que te gustaría que se publique este contenido en tus redes sociales.</p>
          <p className="text-sm text-black">Puedes elegir una fecha dentro de la próxima semana según tu calendario de publicaciones.</p>
        </div>
      </div>
      {/* Barra de filtros y botón de registro en la misma fila */}
      <div className="w-full max-w-5xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 relative">
        <div className="flex items-center gap-3">
          {/* Filtros de servicios (RRSS y SEO) */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={filterRRSS}
                onChange={() => handleFilterChange('RRSS')}
                className="hidden peer"
                id="filter-rrss"
              />
              <span
                className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white"
                style={{ minWidth: 24, minHeight: 24 }}
              >
                {filterRRSS && (
                  <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              <span className="text-base text-black font-medium">RRSS</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
                type="checkbox"
                checked={filterSEO}
                onChange={() => handleFilterChange('SEO')}
                className="hidden peer"
                id="filter-seo"
              />
              <span
                className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white"
                style={{ minWidth: 24, minHeight: 24 }}
              >
                {filterSEO && (
                  <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              <span className="text-base text-black font-medium">SEO</span>
            </label>
          </div>
          
          {/* Separador visual */}
          <div className="flex items-center mx-4">
            <div className="w-px h-6 bg-gray-300"></div>
          </div>
          
          {/* Filtro de Admin */}
          <div className="flex items-center">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={filterAdmin}
                onChange={() => handleFilterChange('ADMIN')}
                className="hidden peer"
                id="filter-admin"
              />
              <span
                className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white"
                style={{ minWidth: 24, minHeight: 24 }}
              >
                {filterAdmin && (
                  <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
              <span className="text-base text-black font-medium">Admin</span>
            </label>
          </div>
          
          
          {/* Búsqueda */}
          <div className="flex items-center bg-white rounded-2xl shadow px-3 py-1.5 ml-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="#44aaf6" className="w-6 h-6 mr-2">
              <circle cx="11" cy="11" r="7" stroke="#44aaf6" strokeWidth="2" />
              <line x1="18" y1="18" x2="15.5" y2="15.5" stroke="#44aaf6" strokeWidth="2" strokeLinecap="round" />
            </svg>
          <input
              type="text"
              placeholder="Buscar por nombre..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="bg-transparent outline-none text-gray-800 placeholder-gray-400 w-32 sm:w-40"
            />
          </div>
        </div>
        {/* Botón Registrar usuario a la derecha, más distintivo */}
        <div className="flex justify-end gap-4">
          <button
            onClick={toggleDeleteMode}
            className={`px-8 py-3 rounded-full font-bold text-lg shadow-lg border-2 transition-all ${
              isDeleteMode 
                ? 'bg-gray-500 text-white border-gray-500 hover:bg-gray-600' 
                : 'bg-red-500 text-white border-red-500 hover:bg-red-600'
            }`}
            style={{ minWidth: 200 }}
          >
            {isDeleteMode ? 'Cancelar eliminación' : 'Eliminar usuarios'}
          </button>
          <button
            onClick={() => {
              if (showRegisterModal) {
                // Si está abierto, cerrarlo y resetear el formulario
                setShowRegisterModal(false);
                setRegisterForm({ 
                  name: '',
                  email: '', 
                  password: '', 
                  role: 'USER', 
                  planType: 'RRSS', 
                  plan: 'RRSS_4M', 
                  useOwnPhotos: false, 
                  instagramId: '', 
                  facebookId: '', 
                  projectId: '', 
                  seoProjectId: '',
                  phone: '', 
                  phonePrefix: '+34',
                  selectedPlans: [],
                  seoPlan: 'SEO_2M',
                  rrssPlan: 'RRSS_4M',
                  seo: [],
                  web: '',
                  autoposting: false,
                  confirmationMailAddress: '',
                  confirmationPhone: '',
                  confirmationPhonePrefix: '+34',
                });
                setRegisterAutopostingManual(false);
                setRegisterConfirmationEnabled(false);
                setRegisterError('');
              } else {
                // Si está cerrado, abrirlo y cerrar los demás
                setEditingUserId(null);
                setEmailingUserId(null);
                setEmailDestination('');
                setNewPassword('');
                setEmailLoading(false);
                
                // Resetear formularios
                setEditForm({
                  email: '',
                  name: '',
                  password: '',
                  instagramId: '',
                  facebookId: '',
                  projectId: '',
                  phone: '',
                  plan: '',
                  seo: [],
                  useOwnPhotos: false,
                  web: '',
                  autoposting: true,
                  confirmationMailAddress: '',
                });
                setConfirmationEnabled(false);
                setEditError('');
                
                // Cerrar modo de eliminación
                setIsDeleteMode(false);
                setUsersToDelete(new Set());
                setAllUsersForDelete([]);
                
                setShowRegisterModal(true);
              }
            }}
            className="px-8 py-3 rounded-full bg-[#44aaf6] text-white font-bold text-lg shadow-lg border-2 border-[#44aaf6] hover:bg-blue-600 transition-all"
            style={{ minWidth: 200 }}
          >
            {showRegisterModal ? 'Cerrar registro' : 'Registrar usuario'}
          </button>
        </div>
      </div>
      {/* Formulario de registro desplegable */}
      <div
        className={`w-full max-w-2xl mx-auto transition-all duration-500 ${showRegisterModal ? 'opacity-100 mt-2 mb-20 h-auto overflow-visible' : 'opacity-0 mb-0 mt-0 h-0 overflow-hidden pointer-events-none'}`}
        style={{ pointerEvents: showRegisterModal ? 'auto' : 'none', zIndex: 50, position: 'relative' }}
      >
        <div className="w-full h-full">
          <div className="bg-white rounded-3xl shadow-2xl w-full animate-fade-in flex flex-col gap-2" style={{ borderRadius: '2rem', padding: '2rem', zIndex: 50, position: 'relative' }}>
            <h2 className="text-2xl font-bold text-[#44aaf6] mb-4 text-center">Registrar nuevo usuario</h2>
            <form onSubmit={handleRegister} className="flex flex-col gap-4">
              {/* Campo oculto para prevenir envío múltiple */}
              <input type="hidden" name="submitted" value={registerLoading ? 'true' : 'false'} />
              <div className="relative">
                <input
                  type="text"
                  required
                  placeholder="Nombre completo"
                  className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  value={registerForm.name}
                  onChange={e => setRegisterForm(f => ({ ...f, name: e.target.value }))}
                  disabled={registerLoading}
                />
                {registerForm.name.length > 0 && !registerLoading && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#44aaf6] text-xl"
                    onClick={() => setRegisterForm(f => ({ ...f, name: '' }))}
                    tabIndex={0}
                    aria-label="Limpiar nombre"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type="email"
                  required
                  placeholder="Email"
                  className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  value={registerForm.email}
                  onChange={e => setRegisterForm(f => ({ ...f, email: e.target.value }))}
                  disabled={registerLoading}
                />
                {registerForm.email.length > 0 && !registerLoading && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#44aaf6] text-xl"
                    onClick={() => setRegisterForm(f => ({ ...f, email: '' }))}
                    tabIndex={0}
                    aria-label="Limpiar email"
                  >
                    ×
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Contraseña"
                  className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full pr-20 disabled:opacity-50 disabled:cursor-not-allowed"
                  value={registerForm.password}
                  onChange={e => setRegisterForm(f => ({ ...f, password: e.target.value }))}
                  disabled={registerLoading}
                />
                {registerForm.password.length > 0 && !registerLoading && (
                  <>
                    <button
                      type="button"
                      className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#44aaf6] transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={0}
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      <EyeIcon open={showPassword} />
                    </button>
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#44aaf6] text-xl"
                      onClick={() => setRegisterForm(f => ({ ...f, password: '' }))}
                      tabIndex={0}
                      aria-label="Limpiar contraseña"
                    >
                      ×
                    </button>
                  </>
                )}
              </div>
              {/* Texto informativo y opción de email alternativo (alineado con los inputs) */}
              <div className="w-full">
                <span className="block text-xs text-[#60a5fa] font-normal mb-3" style={{ opacity: 0.85 }}>
                 &nbsp; <button
                    type="button"
                    className="underline hover:text-blue-700 font-medium focus:outline-none"
                    style={{ fontSize: '0.95em' }}
                    onClick={() => setShowAltEmail(prev => !prev)}
                  >
                    ¿Quieres enviar las credenciales a una dirección de correo diferente?
                  </button>
                </span>
                {showAltEmail && (
                  <input
                    type="email"
                    className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full mb-1"
                    placeholder="Email al que enviar credenciales"
                    value={altEmail}
                    onChange={e => setAltEmail(e.target.value)}
                  />
                )}
              </div>
              {/* Selector de rol con tarjetas */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de usuario</label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                      registerForm.role === 'USER'
                        ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    onClick={() => !registerLoading && setRegisterForm(f => ({ ...f, role: 'USER' }))}
                  >
                  <input
                    type="radio"
                    name="role"
                    value="USER"
                    checked={registerForm.role === 'USER'}
                    onChange={() => setRegisterForm(f => ({ ...f, role: 'USER' }))}
                      className="hidden"
                  />
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        registerForm.role === 'USER' ? 'bg-[#44aaf6] text-white' : 'bg-gray-100 text-gray-400'
                      }`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <div className={`font-semibold transition-colors duration-300 ${
                          registerForm.role === 'USER' ? 'text-[#44aaf6]' : 'text-gray-700'
                        }`}>
                          Usuario
                        </div>
                        <div className="text-xs text-gray-500">Acceso estándar</div>
                      </div>
                    </div>
                    {registerForm.role === 'USER' && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  <div
                    className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                      registerForm.role === 'ADMIN'
                        ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    onClick={() => !registerLoading && setRegisterForm(f => ({ ...f, role: 'ADMIN', plan: '' }))}
                  >
                  <input
                    type="radio"
                    name="role"
                    value="ADMIN"
                    checked={registerForm.role === 'ADMIN'}
                    onChange={() => setRegisterForm(f => ({ ...f, role: 'ADMIN', plan: '' }))}
                      className="hidden"
                  />
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        registerForm.role === 'ADMIN' ? 'bg-[#44aaf6] text-white' : 'bg-gray-100 text-gray-400'
                      }`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <div>
                        <div className={`font-semibold transition-colors duration-300 ${
                          registerForm.role === 'ADMIN' ? 'text-[#44aaf6]' : 'text-gray-700'
                        }`}>
                          Administrador
                        </div>
                        <div className="text-xs text-gray-500">Acceso completo</div>
                      </div>
                    </div>
                    {registerForm.role === 'ADMIN' && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Teléfono - Siempre visible para usuarios no-admin */}
              {registerForm.role !== 'ADMIN' && (
                <div className="w-full">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Teléfono</label>
                  <div className="flex items-center rounded-xl border border-gray-300 bg-white focus-within:border-[#44aaf6] overflow-hidden">
                    <select
                      value={registerForm.phonePrefix}
                      onChange={e => setRegisterForm(f => ({ ...f, phonePrefix: e.target.value }))}
                      className="appearance-none bg-transparent px-3 py-2 text-lg focus:outline-none cursor-pointer min-w-[60px] text-gray-700"
                      style={{border: 'none', outline: 'none'}}>
                      {countryPrefixes.map(country => (
                        <option key={country.prefix} value={country.prefix}>{country.prefix}</option>
                      ))}
                    </select>
                    <span className="text-gray-300 select-none" style={{fontWeight: 'bold'}}>|</span>
                    <input
                      type="tel"
                      required
                      placeholder="611 45 45 45"
                      className="flex-1 px-4 py-2 text-lg bg-transparent focus:outline-none border-none"
                      value={registerForm.phone}
                      onChange={e => {
                        const value = e.target.value;
                        const cleaned = value.replace(/[^\d\s\-]/g, '');
                        setRegisterForm(f => ({ ...f, phone: cleaned }));
                      }}
                      maxLength={15}
                    />
                  </div>
                </div>
              )}
              {/* Campo Web - Solo para usuarios no-admin */}
              {registerForm.role !== 'ADMIN' && (
                <div className="w-full">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Web</label>
                  <input
                    type="text"
                    placeholder="tusitio.com"
                    className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                    value={registerForm.web || ''}
                    onChange={e => setRegisterForm(f => ({ ...f, web: e.target.value }))}
                    maxLength={200}
                  />
                </div>
              )}

              {/* Selector de plan solo si no es admin */}
              {registerForm.role !== 'ADMIN' && (
                <>
                  {/* Selector de tipo de plan */}
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de servicio</label>
                    <div className="grid grid-cols-3 gap-3">
                      <div
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                          registerForm.planType === 'RRSS'
                            ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setRegisterForm(f => ({ ...f, planType: 'RRSS', plan: 'RRSS_4M' }))}
                      >
                      <input
                        type="radio"
                        name="planType"
                        value="RRSS"
                        checked={registerForm.planType === 'RRSS'}
                          onChange={() => setRegisterForm(f => ({ ...f, planType: 'RRSS', plan: 'RRSS_4M' }))}
                          className="hidden"
                      />
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                            registerForm.planType === 'RRSS' ? 'bg-[#44aaf6] text-white' : 'bg-gray-100 text-gray-400'
                          }`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                            </svg>
                          </div>
                          <div>
                            <div className={`font-semibold transition-colors duration-300 ${
                              registerForm.planType === 'RRSS' ? 'text-[#44aaf6]' : 'text-gray-700'
                            }`}>
                              Redes sociales
                            </div>
                          </div>
                        </div>
                        {registerForm.planType === 'RRSS' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                          registerForm.planType === 'SEO'
                            ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setRegisterForm(f => ({ ...f, planType: 'SEO', plan: 'SEO_2M' }))}
                      >
                      <input
                        type="radio"
                        name="planType"
                        value="SEO"
                        checked={registerForm.planType === 'SEO'}
                          onChange={() => setRegisterForm(f => ({ ...f, planType: 'SEO', plan: 'SEO_2M' }))}
                          className="hidden"
                      />
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                            registerForm.planType === 'SEO' ? 'bg-[#44aaf6] text-white' : 'bg-gray-100 text-gray-400'
                          }`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </div>
                          <div>
                            <div className={`font-semibold transition-colors duration-300 ${
                              registerForm.planType === 'SEO' ? 'text-[#44aaf6]' : 'text-gray-700'
                            }`}>
                              SEO
                            </div>
                          </div>
                        </div>
                        {registerForm.planType === 'SEO' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      
                      {/* Nueva opción: SEO y Redes Sociales */}
                      <div
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                          registerForm.planType === 'BOTH'
                            ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setRegisterForm(f => ({ 
                          ...f, 
                          planType: 'BOTH', 
                          plan: 'RRSS_4M',
                          selectedPlans: ['SEO_2M', 'RRSS_4M']
                        }))}
                      >
                        <input
                          type="radio"
                          name="planType"
                          value="BOTH"
                          checked={registerForm.planType === 'BOTH'}
                          onChange={() => setRegisterForm(f => ({ 
                            ...f, 
                            planType: 'BOTH', 
                            plan: 'RRSS_4M',
                            selectedPlans: ['SEO_2M', 'RRSS_4M']
                          }))}
                          className="hidden"
                        />
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                            registerForm.planType === 'BOTH' ? 'bg-[#44aaf6] text-white' : 'bg-gray-100 text-gray-400'
                          }`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                            </svg>
                          </div>
                          <div>
                            <div className={`font-semibold transition-colors duration-300 ${
                              registerForm.planType === 'BOTH' ? 'text-[#44aaf6]' : 'text-gray-700'
                            }`}>
                              SEO y Redes
                            </div>
                            <div className={`text-xs transition-colors duration-300 ${
                              registerForm.planType === 'BOTH' ? 'text-[#44aaf6]' : 'text-gray-500'
                            }`}>
                              Ambos servicios
                            </div>
                          </div>
                        </div>
                        {registerForm.planType === 'BOTH' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                 

                  {/* Selector de plan específico */}
                  {registerForm.planType && registerForm.planType !== 'BOTH' && (
                    <div className="space-y-3">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Plan de publicaciones</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {registerForm.planType === 'SEO' && (
                      <>
                            <div
                              className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                                registerForm.plan === 'SEO_2M'
                                  ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                              onClick={() => setRegisterForm(f => ({ ...f, plan: 'SEO_2M' }))}
                            >
                          <input
                            type="radio"
                            name="plan"
                            value="SEO_2M"
                            checked={registerForm.plan === 'SEO_2M'}
                            onChange={() => setRegisterForm(f => ({ ...f, plan: 'SEO_2M' }))}
                                className="hidden"
                          />
                              <div className="text-center">
                                <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                                  registerForm.plan === 'SEO_2M' ? 'text-[#44aaf6]' : 'text-gray-700'
                                }`}>
                                  2
                                </div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                              </div>
                              {registerForm.plan === 'SEO_2M' && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            
                            <div
                              className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                                registerForm.plan === 'SEO_4M'
                                  ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                              onClick={() => setRegisterForm(f => ({ ...f, plan: 'SEO_4M' }))}
                            >
                          <input
                            type="radio"
                            name="plan"
                            value="SEO_4M"
                            checked={registerForm.plan === 'SEO_4M'}
                            onChange={() => setRegisterForm(f => ({ ...f, plan: 'SEO_4M' }))}
                                className="hidden"
                          />
                              <div className="text-center">
                                <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                                  registerForm.plan === 'SEO_4M' ? 'text-[#44aaf6]' : 'text-gray-700'
                                }`}>
                                  4
                                </div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                              </div>
                              {registerForm.plan === 'SEO_4M' && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                      </>
                    )}
                        
                    {registerForm.planType === 'RRSS' && (
                      <>
                            <div
                              className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                                registerForm.plan === 'RRSS_4M'
                                  ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                              onClick={() => setRegisterForm(f => ({ ...f, plan: 'RRSS_4M' }))}
                            >
                          <input
                            type="radio"
                            name="plan"
                            value="RRSS_4M"
                            checked={registerForm.plan === 'RRSS_4M'}
                            onChange={() => setRegisterForm(f => ({ ...f, plan: 'RRSS_4M' }))}
                                className="hidden"
                          />
                              <div className="text-center">
                                <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                                  registerForm.plan === 'RRSS_4M' ? 'text-[#44aaf6]' : 'text-gray-700'
                                }`}>
                                  4
                                </div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                              </div>
                              {registerForm.plan === 'RRSS_4M' && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            
                            <div
                              className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                                registerForm.plan === 'RRSS_6M'
                                  ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                              onClick={() => setRegisterForm(f => ({ ...f, plan: 'RRSS_6M' }))}
                            >
                          <input
                            type="radio"
                            name="plan"
                            value="RRSS_6M"
                            checked={registerForm.plan === 'RRSS_6M'}
                            onChange={() => setRegisterForm(f => ({ ...f, plan: 'RRSS_6M' }))}
                                className="hidden"
                          />
                              <div className="text-center">
                                <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                                  registerForm.plan === 'RRSS_6M' ? 'text-[#44aaf6]' : 'text-gray-700'
                                }`}>
                                  6
                                </div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                              </div>
                              {registerForm.plan === 'RRSS_6M' && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            
                            <div
                              className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                                registerForm.plan === 'RRSS_12M'
                                  ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                              onClick={() => setRegisterForm(f => ({ ...f, plan: 'RRSS_12M' }))}
                            >
                          <input
                            type="radio"
                            name="plan"
                            value="RRSS_12M"
                            checked={registerForm.plan === 'RRSS_12M'}
                            onChange={() => setRegisterForm(f => ({ ...f, plan: 'RRSS_12M' }))}
                                className="hidden"
                          />
                              <div className="text-center">
                                <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                                  registerForm.plan === 'RRSS_12M' ? 'text-[#44aaf6]' : 'text-gray-700'
                                }`}>
                                  12
                                </div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                              </div>
                              {registerForm.plan === 'RRSS_12M' && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                                            </>
                    )}
                  </div>
                </div>
              )}

              {/* Selector de planes individuales para BOTH */}
              {registerForm.planType === 'BOTH' && (
                <div className="space-y-6">
                  {/* Plan SEO */}
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Plan SEO</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                          registerForm.seoPlan === 'SEO_2M'
                            ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setRegisterForm(f => ({ 
                          ...f, 
                          seoPlan: 'SEO_2M',
                          selectedPlans: ['SEO_2M', f.rrssPlan]
                        }))}
                      >
                        <input
                          type="radio"
                          name="seoPlan"
                          value="SEO_2M"
                          checked={registerForm.seoPlan === 'SEO_2M'}
                          onChange={() => setRegisterForm(f => ({ 
                            ...f, 
                            seoPlan: 'SEO_2M',
                            selectedPlans: ['SEO_2M', f.rrssPlan]
                          }))}
                          className="hidden"
                        />
                        <div className="text-center">
                          <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                            registerForm.seoPlan === 'SEO_2M' ? 'text-[#44aaf6]' : 'text-gray-700'
                          }`}>
                            2
                          </div>
                          <div className="text-sm text-gray-600">publicaciones/mes</div>
                        </div>
                        {registerForm.seoPlan === 'SEO_2M' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      
                      <div
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                          registerForm.seoPlan === 'SEO_4M'
                            ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setRegisterForm(f => ({ 
                          ...f, 
                          seoPlan: 'SEO_4M',
                          selectedPlans: ['SEO_4M', f.rrssPlan]
                        }))}
                      >
                        <input
                          type="radio"
                          name="seoPlan"
                          value="SEO_4M"
                          checked={registerForm.seoPlan === 'SEO_4M'}
                          onChange={() => setRegisterForm(f => ({ 
                            ...f, 
                            seoPlan: 'SEO_4M',
                            selectedPlans: ['SEO_4M', f.rrssPlan]
                          }))}
                          className="hidden"
                        />
                        <div className="text-center">
                          <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                            registerForm.seoPlan === 'SEO_4M' ? 'text-[#44aaf6]' : 'text-gray-700'
                          }`}>
                            4
                          </div>
                          <div className="text-sm text-gray-600">publicaciones/mes</div>
                        </div>
                        {registerForm.seoPlan === 'SEO_4M' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Plan RRSS */}
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Plan Redes Sociales</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                          registerForm.rrssPlan === 'RRSS_4M'
                            ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setRegisterForm(f => ({ 
                          ...f, 
                          rrssPlan: 'RRSS_4M',
                          selectedPlans: [f.seoPlan, 'RRSS_4M']
                        }))}
                      >
                        <input
                          type="radio"
                          name="rrssPlan"
                          value="RRSS_4M"
                          checked={registerForm.rrssPlan === 'RRSS_4M'}
                          onChange={() => setRegisterForm(f => ({ 
                            ...f, 
                            rrssPlan: 'RRSS_4M',
                            selectedPlans: [f.seoPlan, 'RRSS_4M']
                          }))}
                          className="hidden"
                        />
                        <div className="text-center">
                          <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                            registerForm.rrssPlan === 'RRSS_4M' ? 'text-[#44aaf6]' : 'text-gray-700'
                          }`}>
                            4
                          </div>
                          <div className="text-sm text-gray-600">publicaciones/mes</div>
                        </div>
                        {registerForm.rrssPlan === 'RRSS_4M' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      
                      <div
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                          registerForm.rrssPlan === 'RRSS_6M'
                            ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setRegisterForm(f => ({ 
                          ...f, 
                          rrssPlan: 'RRSS_6M',
                          selectedPlans: [f.seoPlan, 'RRSS_6M']
                        }))}
                      >
                        <input
                          type="radio"
                          name="rrssPlan"
                          value="RRSS_6M"
                          checked={registerForm.rrssPlan === 'RRSS_6M'}
                          onChange={() => setRegisterForm(f => ({ 
                            ...f, 
                            rrssPlan: 'RRSS_6M',
                            selectedPlans: [f.seoPlan, 'RRSS_6M']
                          }))}
                          className="hidden"
                        />
                        <div className="text-center">
                          <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                            registerForm.rrssPlan === 'RRSS_6M' ? 'text-[#44aaf6]' : 'text-gray-700'
                          }`}>
                            6
                          </div>
                          <div className="text-sm text-gray-600">publicaciones/mes</div>
                        </div>
                        {registerForm.rrssPlan === 'RRSS_6M' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      
                      <div
                        className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md ${
                          registerForm.rrssPlan === 'RRSS_12M'
                            ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                        onClick={() => setRegisterForm(f => ({ 
                          ...f, 
                          rrssPlan: 'RRSS_12M',
                          selectedPlans: [f.seoPlan, 'RRSS_12M']
                        }))}
                      >
                        <input
                          type="radio"
                          name="rrssPlan"
                          value="RRSS_12M"
                          checked={registerForm.rrssPlan === 'RRSS_12M'}
                          onChange={() => setRegisterForm(f => ({ 
                            ...f, 
                            rrssPlan: 'RRSS_12M',
                            selectedPlans: [f.seoPlan, 'RRSS_12M']
                          }))}
                          className="hidden"
                        />
                        <div className="text-center">
                          <div className={`text-2xl font-bold mb-1 transition-colors duration-300 ${
                            registerForm.rrssPlan === 'RRSS_12M' ? 'text-[#44aaf6]' : 'text-gray-700'
                          }`}>
                            12
                          </div>
                          <div className="text-sm text-gray-600">publicaciones/mes</div>
                        </div>
                        {registerForm.rrssPlan === 'RRSS_12M' && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>



                  {/* Project ID para RRSS (BOTH) */}
                  <div className="w-full">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Project ID (Redes Sociales)</label>
                    <input
                      type="text"
                      required
                      placeholder="Project ID RRSS"
                      className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                      value={registerForm.projectId}
                      onChange={e => setRegisterForm(f => ({ ...f, projectId: e.target.value }))}
                      maxLength={100}
                    />
                  </div>

                  {/* Project ID para SEO (BOTH) */}
                  <div className="w-full">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Project ID (SEO)</label>
                    <input
                      type="text"
                      required
                      placeholder="Project ID SEO"
                      className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                      value={registerForm.seoProjectId}
                      onChange={e => setRegisterForm(f => ({ ...f, seoProjectId: e.target.value }))}
                      maxLength={100}
                    />
                  </div>

                  {/* Apartado de Redes para BOTH */}
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Redes Sociales
                      <span className="text-red-500 ml-1">*</span>
                      <span className="text-xs text-gray-500 ml-2">(al menos una obligatoria)</span>
                    </label>
                    <div className="flex flex-col gap-3 mt-0">
                      <input
                        type="text"
                        placeholder="Instagram ID"
                        className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                        value={registerForm.instagramId || ''}
                        onChange={e => setRegisterForm(f => ({ ...f, instagramId: e.target.value }))}
                        maxLength={100}
                      />
                      <input
                        type="text"
                        placeholder="Facebook ID"
                        className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                        value={registerForm.facebookId || ''}
                        onChange={e => setRegisterForm(f => ({ ...f, facebookId: e.target.value }))}
                        maxLength={100}
                      />
                    </div>
                  </div>
                </div>
              )}
                </>
              )}
              
              {/* Selector de fotos propias solo para usuarios */}
              {registerForm.role === 'USER' && (
                <div className="flex flex-col gap-3">
                  <p className="text-base text-gray-700 font-bold text-center mt-4">Tipo de imágenes</p>
                  <div className="flex gap-4 justify-center">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="useOwnPhotos"
                        value="false"
                        checked={!registerForm.useOwnPhotos}
                        onChange={() => setRegisterForm(f => ({ ...f, useOwnPhotos: false }))}
                        className="hidden peer"
                      />
                      <span
                        className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white"
                        style={{ minWidth: 24, minHeight: 24 }}
                      >
                        {!registerForm.useOwnPhotos && (
                          <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className="text-base text-black font-medium">ChatGPT</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="useOwnPhotos"
                        value="true"
                        checked={registerForm.useOwnPhotos}
                        onChange={() => setRegisterForm(f => ({ ...f, useOwnPhotos: true }))}
                        className="hidden peer"
                      />
                      <span
                        className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white"
                        style={{ minWidth: 24, minHeight: 24 }}
                      >
                        {registerForm.useOwnPhotos && (
                          <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className="text-base text-black font-medium">Propias</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Selector de autoposting para usuarios */}
              {registerForm.role === 'USER' && (
                <div className="flex flex-col gap-3">
                  <p className="text-base text-gray-700 font-bold text-center mt-4">Creación automática de publicaciones</p>
                  <div className="flex gap-4 justify-center">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="autoposting"
                        value="false"
                        checked={!registerForm.autoposting}
                        onChange={() => {
                          setRegisterAutopostingManual(true);
                          setRegisterForm(f => ({ ...f, autoposting: false }));
                        }}
                        className="hidden peer"
                      />
                      <span
                        className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white"
                        style={{ minWidth: 24, minHeight: 24 }}
                      >
                        {!registerForm.autoposting && (
                          <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className="text-base text-black font-medium">Desactivada</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="autoposting"
                        value="true"
                        checked={registerForm.autoposting}
                        onChange={() => {
                          setRegisterAutopostingManual(true);
                          setRegisterForm(f => ({ ...f, autoposting: true }));
                        }}
                        className="hidden peer"
                      />
                      <span
                        className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white"
                        style={{ minWidth: 24, minHeight: 24 }}
                      >
                        {registerForm.autoposting && (
                          <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className="text-base text-black font-medium">Activada</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Selector de confirmación de posts — Solo para usuarios RRSS o BOTH */}
              {registerForm.role === 'USER' && (registerForm.planType === 'RRSS' || registerForm.planType === 'BOTH') && (
                <div className="flex flex-col gap-3">
                  <p className="text-base text-gray-700 font-bold text-center mt-4">Esperar confirmación del cliente</p>
                  <div className="flex gap-4 justify-center">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="registerConfirmation"
                        checked={registerConfirmationEnabled === true}
                        onChange={() => {
                          setRegisterConfirmationEnabled(true);
                          if (!registerForm.confirmationMailAddress) {
                            setRegisterForm(f => ({ ...f, confirmationMailAddress: '', confirmationPhone: '' }));
                          }
                        }}
                        className="hidden peer"
                      />
                      <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                        {registerConfirmationEnabled === true && (
                          <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className="text-base text-black font-medium">Activado</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="registerConfirmation"
                        checked={registerConfirmationEnabled === false}
                        onChange={() => {
                          setRegisterConfirmationEnabled(false);
                          setRegisterForm(f => ({ ...f, confirmationMailAddress: '', confirmationPhone: '' }));
                        }}
                        className="hidden peer"
                      />
                      <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                        {registerConfirmationEnabled === false && (
                          <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className="text-base text-black font-medium">Desactivado</span>
                    </label>
                  </div>
                  
                  {/* Campos de confirmación — Solo visible si está activada */}
                  {registerConfirmationEnabled && (
                    <div className="mt-4 flex flex-col gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email de confirmación
                        </label>
                        <input
                          type="email"
                          placeholder="Email para confirmaciones"
                          className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full max-w-md mx-auto"
                          value={registerForm.confirmationMailAddress || ''}
                          onChange={e => setRegisterForm(f => ({ ...f, confirmationMailAddress: e.target.value }))}
                          maxLength={100}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirmación por WhatsApp
                        </label>
                        <div className="flex items-center rounded-xl border border-gray-300 bg-white focus-within:border-[#44aaf6] overflow-hidden max-w-md mx-auto">
                          <select
                            value={registerForm.confirmationPhonePrefix}
                            onChange={e => setRegisterForm(f => ({ ...f, confirmationPhonePrefix: e.target.value }))}
                            className="appearance-none bg-transparent px-3 py-2 text-lg focus:outline-none cursor-pointer min-w-[60px] text-gray-700"
                            style={{border: 'none', outline: 'none'}}>
                            {countryPrefixes.map(country => (
                              <option key={country.prefix} value={country.prefix}>{country.prefix}</option>
                            ))}
                          </select>
                          <span className="text-gray-300 select-none" style={{fontWeight: 'bold'}}>|</span>
                          <input
                            type="tel"
                            placeholder="611 48 07 09"
                            className="flex-1 px-4 py-2 text-lg bg-transparent focus:outline-none border-none"
                            value={registerForm.confirmationPhone || ''}
                            onChange={e => {
                              const value = e.target.value;
                              const cleaned = value.replace(/[^\d\s\-]/g, '');
                              setRegisterForm(f => ({ ...f, confirmationPhone: cleaned }));
                            }}
                            maxLength={15}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Mostrar el selector solo si el rol es USER y el plan es SEO o BOTH */}
              {registerForm.role === 'USER' && (registerForm.planType === 'SEO' || registerForm.planType === 'BOTH') && (
                <div className="flex flex-col gap-3">
                  <p className="text-base text-gray-700 font-bold text-center mt-4">Opciones de SEO</p>
                  <div className="flex gap-6 justify-center">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={registerForm.seo.includes('BLOG')}
                        onChange={() =>
                          setRegisterForm((f) => ({
                            ...f,
                            seo: ['BLOG'],
                          }))
                        }
                        className="hidden peer"
                      />
                      <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                        {registerForm.seo.includes('BLOG') && (
                          <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className="text-base text-black font-medium">Blog</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={registerForm.seo.includes('BLOQUE_TEXTO')}
                        onChange={() =>
                          setRegisterForm((f) => ({
                            ...f,
                            seo: ['BLOQUE_TEXTO'],
                          }))
                        }
                        className="hidden peer"
                      />
                      <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                        {registerForm.seo.includes('BLOQUE_TEXTO') && (
                          <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className="text-base text-black font-medium">Bloque de texto</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Project ID para SEO */}
              {registerForm.planType === 'SEO' && registerForm.role !== 'ADMIN' && (
                <div className="w-full">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Project ID</label>
                  <input
                    type="text"
                    required
                    placeholder="Project ID"
                    className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                    value={registerForm.projectId}
                    onChange={e => setRegisterForm(f => ({ ...f, projectId: e.target.value }))}
                    maxLength={100}
                  />
                </div>
              )}



              
              {/* Project ID para RRSS */}
              {registerForm.planType === 'RRSS' && registerForm.role !== 'ADMIN' && (
                <div className="w-full">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Project ID</label>
                  <input
                    type="text"
                    required
                    placeholder="Project ID"
                    className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                    value={registerForm.projectId}
                    onChange={e => setRegisterForm(f => ({ ...f, projectId: e.target.value }))}
                    maxLength={100}
                  />
                </div>
              )}

              {/* Apartado de Redes solo si es RRSS y el rol no es ADMIN */}
              {registerForm.planType === 'RRSS' && registerForm.role !== 'ADMIN' && (
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Redes
                    <span className="text-red-500 ml-1">*</span>
                    <span className="text-xs text-gray-500 ml-2">(al menos una obligatoria)</span>
                  </label>
                  <div className="flex flex-col gap-3 mt-0">
                    <input
                      type="text"
                      placeholder="Instagram ID"
                      className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                      value={registerForm.instagramId || ''}
                      onChange={e => setRegisterForm(f => ({ ...f, instagramId: e.target.value }))}
                      maxLength={100}
                    />
                    <input
                      type="text"
                      placeholder="Facebook ID"
                      className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                      value={registerForm.facebookId || ''}
                      onChange={e => setRegisterForm(f => ({ ...f, facebookId: e.target.value }))}
                      maxLength={100}
                    />
                  </div>
                </div>
              )}
              
              {registerError && <div className="text-red-500 text-center text-sm font-semibold">{registerError}</div>}
              <button
                type="submit"
                className="mt-2 px-6 py-2 rounded-full bg-[#44aaf6] text-white font-bold text-lg shadow hover:bg-blue-600 transition-all disabled:opacity-60"
                disabled={registerLoading}
              >
                {registerLoading ? 'Registrando...' : 'Registrar'}
              </button>
            </form>
          </div>
        </div>
      </div>
      {/* Filtro de fecha seleccionado */}
      {selectedDate && (
        <div className="w-full max-w-5xl mx-auto flex justify-start mb-4">
          <span className="flex items-center bg-blue-50 text-[#44aaf6] font-semibold rounded-full px-4 py-2 text-base shadow mr-2">
            Fecha seleccionada: {new Date(selectedDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
            <button
              className="ml-2 text-[#44aaf6] hover:text-blue-600 text-lg font-bold focus:outline-none"
              onClick={() => setSelectedDate(null)}
              aria-label="Eliminar filtro de fecha"
            >
              ×
            </button>
          </span>
        </div>
      )}
      {/* Mensaje de confirmación de eliminación */}
      {isDeleteMode && usersToDelete.size > 0 && (
        <div className="w-full max-w-5xl mx-auto mb-6 bg-red-50 border border-red-200 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-red-700 mb-4">¿Quieres eliminar a los siguientes usuarios?</h3>
          <div className="mb-4">
            {Array.from(usersToDelete).map(userId => {
              const user = allUsersForDelete.find(u => u.id === userId);
              return user ? (
                <div key={userId} className="text-red-600 font-medium mb-1">
                  • {formatUserNameAndEmail(user)} {user.plan && `(${getPlanType(user.plan)})`}
                </div>
              ) : null;
            })}
          </div>
          <div className="flex gap-4">
            <button
              onClick={handleConfirmDelete}
              disabled={deleteConfirmLoading}
              className="px-6 py-2 rounded-full bg-red-500 text-white font-bold shadow hover:bg-red-600 transition-all disabled:opacity-60"
            >
              {deleteConfirmLoading ? 'Eliminando...' : `Eliminar ${usersToDelete.size} usuario${usersToDelete.size > 1 ? 's' : ''}`}
            </button>
            <button
              onClick={handleCancelDelete}
              className="px-6 py-2 rounded-full border border-gray-300 text-gray-700 font-bold shadow hover:bg-gray-50 transition-all"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de usuarios */}
      {loading ? (
        <div className="text-lg text-gray-500">Cargando usuarios...</div>
      ) : error ? (
        <div className="text-red-500 font-semibold">{error}</div>
      ) : (
        <div className="w-full max-w-5xl mx-auto flex flex-col gap-8">
          {total === 0 ? (
            <div className="text-center text-gray-400 py-6">No hay usuarios</div>
          ) : paginatedUsers.map(user => (
            <div
              key={user.id}
              className={`bg-white rounded-3xl shadow-lg px-10 py-5 flex flex-col gap-2 animate-fade-in transition-all ${
                isDeleteMode && usersToDelete.has(user.id) 
                  ? 'bg-red-100 border-2 border-red-300' 
                  : isDeleteMode 
                    ? 'hover:bg-red-50 border-2 border-transparent' 
                    : ''
              }`}
              onClick={isDeleteMode ? () => handleUserSelectForDelete(user.id) : undefined}
              style={{ cursor: isDeleteMode ? 'pointer' : 'default' }}
            >
              <div className="flex flex-row justify-between items-center w-full mb-1">
                <div className="flex flex-col gap-1">
                <div className="flex items-center gap-3">
                  <span className="font-bold text-lg text-gray-800">
                    {formatUserNameAndEmail(user)}
                    {user.phone && (
                      <span className="text-sm font-normal text-gray-500 ml-2"></span>
                    )}
                    {user.role === 'ADMIN' && (
                      <span className="text-sm font-normal text-gray-500 ml-2">(Administrador)</span>
                    )}
                    {/* Indicador de usuario multicuenta */}
                    {hasMultiplePlans(user) && (
                      <span className="text-sm font-normal text-[#44aaf6] ml-2">(usuario con varios planes)</span>
                    )}
                  </span>

                </div>
                  {user.role !== 'ADMIN' && (
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span className="font-medium">
                        {user.plan ? (
                          user.plan.startsWith('SEO') ? 'SEO' : 'Redes sociales'
                        ) : 'Sin plan'}
                      </span>
                      <span className="text-gray-400">·</span>
                      <span>
                        {getPublicationsCount(user.plan) ? `${getPublicationsCount(user.plan)} publicaciones/mes` : 'Sin publicaciones'}
                      </span>
                      {user.phone && (
                        <>
                          <span className="text-gray-400">·</span>
                          <span className="flex items-center gap-1">
                            <span role="img" aria-label="Teléfono"></span> 
                            {formatPhoneForDisplay(user.phone)}
                          </span>
                        </>
                      )}
                      <span className="text-gray-400">·</span>
                      <div className="flex items-center gap-2">
                        <span>
                          {user.useOwnPhotos ? 'Imágenes propias' : 'Imágenes por IA'}
                        </span>
                      </div>
                    </div>
                  )}
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        className={`w-10 h-10 rounded-full border font-semibold transition-all shadow flex items-center justify-center
                          ${editingUserId === user.id
                            ? 'bg-[#44aaf6] border-[#44aaf6] text-white'
                            : 'bg-white border-[#44aaf6] text-[#44aaf6] hover:bg-blue-50'}
                        `}
                        onClick={e => { e.stopPropagation(); handleEdit(user); }}
                        title={editingUserId === user.id ? "Cerrar edición" : "Editar usuario"}
                      >
                        <svg
                          className="w-4 h-4 transition-colors duration-200"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        className={`w-10 h-10 rounded-full border font-semibold transition-all shadow flex items-center justify-center
                          ${emailingUserId === user.id
                            ? 'bg-[#44aaf6] border-[#44aaf6] text-white'
                            : 'bg-white border-[#44aaf6] text-[#44aaf6] hover:bg-blue-50'}
                        `}
                        onClick={e => { e.stopPropagation(); handleSendEmail(user); }}
                        title={emailingUserId === user.id ? "Cerrar envío de email" : "Enviar credenciales por correo"}
                      >
                        <svg
                          className="w-4 h-4 transition-colors duration-200"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </button>
                      <button
                        className="px-6 py-2 rounded-full border border-[#44aaf6] text-[#44aaf6] font-semibold hover:bg-blue-50 transition-all shadow bg-white text-base"
                        onClick={() => handleSelect(user)}
                      >
                        Ver calendario
                      </button>
                    </div>
              </div>
              {/* Formulario de edición desplegable */}
              {editingUserId === user.id && (
                <div className="w-full flex justify-center">
                  <form
                    className="w-full max-w-2xl flex flex-col gap-4 p-4 sm:p-6 md:p-8 animate-fade-in"
                    style={{ zIndex: 50, position: 'relative' }}
                    onSubmit={e => { e.preventDefault(); handleSaveEdit(user.id); }}
                    onClick={e => e.stopPropagation()}
                  >
                    {/* Título y subtítulo copiados del formulario de email */}
                    <h2 className="text-3xl font-bold text-[#44aaf6] mb-0 text-center">
                      Editar datos del usuario
                    </h2>
                    
                    {/* Mensaje informativo para usuarios multicuenta */}
                    {hasMultiplePlans(user) && (
                      <p className="text-sm text-black mb-1 text-center" style={{opacity: 0.85}}>
                        Estás editando un usuario con <span className='text-[#44aaf6] font-bold'>varios planes</span>. Si modificas datos comunes (nombre, contraseña...), se editarán automáticamente en el otro plan.
                      </p>
                      
                    )}
                    
                    {/* Formulario simplificado para usuarios ADMIN */}
                    {user.role === 'ADMIN' ? (
                      <>
                        {/* Línea 1: Nombre */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                          <div className="flex-1 min-w-[180px] max-w-[320px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">Nombre</label>
                            <input
                              type="text"
                              required
                              placeholder="Nombre completo"
                              className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full text-center"
                              value={editForm.name}
                              onChange={e => setEditForm((f: EditForm) => ({ ...f, name: e.target.value }))}
                              maxLength={100}
                            />
                          </div>
                        </div>
                        
                        {/* Línea 2: Email, Password */}
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1 min-w-[180px] max-w-[320px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                            <input
                              type="email"
                              required
                              placeholder="Email"
                              className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                              value={editForm.email}
                              onChange={e => setEditForm((f: EditForm) => ({ ...f, email: e.target.value }))}
                              maxLength={100}
                            />
                          </div>
                          <div className="flex-1 min-w-[180px] max-w-[320px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Nueva contraseña</label>
                            <div className="relative">
                              <input
                                type={showEditPassword ? "text" : "password"}
                                placeholder="Nueva contraseña (opcional)"
                                className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full pr-10"
                                value={editForm.password}
                                onChange={e => setEditForm((f: EditForm) => ({ ...f, password: e.target.value }))}
                                maxLength={100}
                              />
                              {editForm.password.length > 0 && (
                                <button
                                  type="button"
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#44aaf6] transition-colors"
                                  onClick={() => setShowEditPassword(!showEditPassword)}
                                  tabIndex={0}
                                  aria-label={showEditPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                >
                                  <EyeIcon open={showEditPassword} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Línea 3: Autoposting para ADMIN */}
                        <div className="mt-4 text-center">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Creación de publicaciones automática
                          </label>
                          <div className="flex gap-4 justify-center">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="radio"
                                name="adminAutoposting"
                                checked={editForm.autoposting === true}
                                onChange={() => setEditForm(f => ({ ...f, autoposting: true }))}
                                className="hidden peer"
                              />
                              <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                {editForm.autoposting === true && (
                                  <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                              <span className="text-base text-black font-medium">Activada</span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="radio"
                                name="adminAutoposting"
                                checked={editForm.autoposting === false}
                                onChange={() => setEditForm(f => ({ ...f, autoposting: false }))}
                                className="hidden peer"
                              />
                              <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                {editForm.autoposting === false && (
                                  <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                              <span className="text-base text-black font-medium">Desactivada</span>
                            </label>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Línea 1: Nombre */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                          <div className="flex-1 min-w-[180px] max-w-[320px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">Nombre</label>
                            <input
                              type="text"
                              required
                              placeholder="Nombre completo"
                              className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full text-center"
                              value={editForm.name}
                              onChange={e => setEditForm((f: EditForm) => ({ ...f, name: e.target.value }))}
                              maxLength={100}
                            />
                          </div>
                        </div>
                        {/* Línea 2: Email, Teléfono */}
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1 min-w-[180px] max-w-[320px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                            <input
                              type="email"
                              required
                              placeholder="Email"
                              className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                              value={editForm.email}
                              onChange={e => setEditForm((f: EditForm) => ({ ...f, email: e.target.value }))}
                              maxLength={100}
                            />
                          </div>
                          <div className="flex-1 min-w-[180px] max-w-[320px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Teléfono</label>
                            <div className="flex items-center rounded-xl border border-gray-300 bg-white focus-within:border-[#44aaf6] overflow-hidden">
                              <select
                                value={editPhonePrefix}
                                onChange={e => {
                                  setEditPhonePrefix(e.target.value);
                                  setEditForm((f: EditForm) => ({ ...f, phone: e.target.value.replace('+', '') + f.phone.replace(/^\d{2,3}/, '') }));
                                }}
                                className="appearance-none bg-transparent px-3 py-2 text-lg focus:outline-none cursor-pointer min-w-[60px] text-gray-700"
                              >
                                {countryPrefixes.map(country => (
                                  <option key={country.prefix} value={country.prefix}>{country.prefix}</option>
                                ))}
                              </select>
                              <span className="text-gray-300 select-none font-bold">|</span>
                              <input
                                type="tel"
                                placeholder="611 45 45 45"
                                className="flex-1 px-4 py-2 text-lg bg-transparent focus:outline-none border-none"
                                value={(function() {
                                  // Detectar el prefijo real
                                  const allowedPrefixes = ['34','33','39','49','44','54','56','52','57','51','58','55'];
                                  let prefix = '';
                                  let rest = editForm.phone;
                                  for (const p of allowedPrefixes) {
                                    if (editForm.phone.startsWith(p)) {
                                      prefix = p;
                                      rest = editForm.phone.slice(p.length);
                                      break;
                                    }
                                  }
                                  if (!prefix) {
                                    // Si no se detecta prefijo, usar los primeros 2 dígitos por defecto
                                    rest = editForm.phone.slice(2);
                                  }
                                  return rest;
                                })()}
                                onChange={e => {
                                  const value = e.target.value.replace(/[^\d\s\-]/g, '');
                                  setEditForm((f: EditForm) => ({ ...f, phone: editPhonePrefix.replace('+', '') + value }));
                                }}
                                maxLength={15}
                              />
                            </div>
                          </div>
                        </div>
                        {/* Campo Web - Solo para usuarios no-admin */}
                        {user.role !== 'ADMIN' && (
                          <div className="w-full">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Web</label>
                            <input
                              type="text"
                              placeholder="tusitio.com"
                              className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                              value={editForm.web || ''}
                              onChange={e => setEditForm((f: EditForm) => ({ ...f, web: e.target.value }))}
                              maxLength={200}
                            />
                          </div>
                        )}
                        {/* Línea 2: Tipo de plan (SEO o RRSS) */}
                        <div className="flex flex-row gap-4 justify-center">
                          <div
                            className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md flex-1 min-w-[120px] max-w-[200px] text-center ${
                              editForm.plan.startsWith('SEO')
                                ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            onClick={() => {
                              // Si ya es SEO, no cambiar publicaciones
                              setEditForm((f: EditForm) => ({ ...f, plan: f.plan.startsWith('SEO') ? f.plan : 'SEO_2M' }));
                            }}
                          >
                            <div className={`font-semibold text-lg ${editForm.plan.startsWith('SEO') ? 'text-[#44aaf6]' : 'text-gray-700'}`}>SEO</div>
                            <div className="text-xs text-gray-500">Optimización web</div>
                            {editForm.plan.startsWith('SEO') && (
                              <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div
                            className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md flex-1 min-w-[120px] max-w-[200px] text-center ${
                              editForm.plan.startsWith('RRSS')
                                ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            onClick={() => {
                              setEditForm((f: EditForm) => ({ ...f, plan: f.plan.startsWith('RRSS') ? f.plan : 'RRSS_4M' }));
                            }}
                          >
                            <div className={`font-semibold text-lg ${editForm.plan.startsWith('RRSS') ? 'text-[#44aaf6]' : 'text-gray-700'}`}>Redes sociales</div>
                            <div className="text-xs text-gray-500">Gestión RRSS</div>
                            {editForm.plan.startsWith('RRSS') && (
                              <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Línea 3: Selección de publicaciones mensuales */}
                        <div className="flex flex-row gap-4 justify-center">
                          {editForm.plan.startsWith('SEO') && (
                            <>
                              <div
                                className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md min-w-[120px] max-w-[180px] text-center ${
                                  editForm.plan === 'SEO_2M'
                                    ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                                onClick={() => setEditForm((f: EditForm) => ({ ...f, plan: 'SEO_2M' }))}
                              >
                                <div className={`text-2xl font-bold mb-1 ${editForm.plan === 'SEO_2M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>2</div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                                {editForm.plan === 'SEO_2M' && (
                                  <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div
                                className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md min-w-[120px] max-w-[180px] text-center ${
                                  editForm.plan === 'SEO_4M'
                                    ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                                onClick={() => setEditForm((f: EditForm) => ({ ...f, plan: 'SEO_4M' }))}
                              >
                                <div className={`text-2xl font-bold mb-1 ${editForm.plan === 'SEO_4M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>4</div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                                {editForm.plan === 'SEO_4M' && (
                                  <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                          {editForm.plan.startsWith('RRSS') && (
                            <>
                              <div
                                className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md min-w-[120px] max-w-[180px] text-center ${
                                  editForm.plan === 'RRSS_4M'
                                    ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                                onClick={() => setEditForm((f: EditForm) => ({ ...f, plan: 'RRSS_4M' }))}
                              >
                                <div className={`text-2xl font-bold mb-1 ${editForm.plan === 'RRSS_4M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>4</div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                                {editForm.plan === 'RRSS_4M' && (
                                  <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div
                                className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md min-w-[120px] max-w-[180px] text-center ${
                                  editForm.plan === 'RRSS_6M'
                                    ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                                onClick={() => setEditForm((f: EditForm) => ({ ...f, plan: 'RRSS_6M' }))}
                              >
                                <div className={`text-2xl font-bold mb-1 ${editForm.plan === 'RRSS_6M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>6</div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                                {editForm.plan === 'RRSS_6M' && (
                                  <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div
                                className={`relative cursor-pointer rounded-xl border-2 p-4 transition-all duration-300 hover:shadow-md min-w-[120px] max-w-[180px] text-center ${
                                  editForm.plan === 'RRSS_12M'
                                    ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                                onClick={() => setEditForm((f: EditForm) => ({ ...f, plan: 'RRSS_12M' }))}
                              >
                                <div className={`text-2xl font-bold mb-1 ${editForm.plan === 'RRSS_12M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>12</div>
                                <div className="text-sm text-gray-600">publicaciones/mes</div>
                                {editForm.plan === 'RRSS_12M' && (
                                  <div className="absolute top-2 right-2 w-5 h-5 bg-[#44aaf6] rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Botón Añadir Plan - Solo para usuarios sin múltiples planes y sin rol ADMIN */}
                        {!hasMultiplePlans(user) && user.role !== 'ADMIN' && (
                          <div className="mt-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleToggleAddPlanSection(user.id)}
                              className="px-6 py-3 rounded-full bg-[#44aaf6] text-white font-bold text-lg shadow hover:bg-blue-600 transition-all flex items-center justify-center gap-2 mx-auto"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              {showAddPlanSection === user.id ? 'Ocultar Planes' : 'Añadir Plan'}
                            </button>
                            <p className="text-xs text-gray-500 mt-2">
                              {showAddPlanSection === user.id ? 'Oculta las opciones de plan' : 'Añade un plan adicional a este usuario'}
                            </p>
                          </div>
                        )}

                        {/* Sección de añadir plan desplegable */}
                        {showAddPlanSection === user.id && !hasMultiplePlans(user) && user.role !== 'ADMIN' && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200 animate-fade-in">
                            <h4 className="font-semibold text-gray-700 mb-3 text-center">
                              Selecciona el plan a añadir:
                            </h4>
                            
                            {/* Mostrar solo el plan disponible */}
                            {user.plan?.startsWith('RRSS') ? (
                              // Si tiene RRSS, mostrar opciones de SEO
                              <div className="space-y-4">
                                <h5 className="font-medium text-gray-600 text-center">Planes SEO disponibles:</h5>
                                <div className="grid grid-cols-2 gap-3">
                                  <button
                                    type="button"
                                    onClick={() => setNewPlanToAdd('SEO_2M')}
                                    className={`p-4 rounded-xl border-2 transition-all ${
                                      newPlanToAdd === 'SEO_2M'
                                        ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <div className={`text-xl font-bold mb-1 ${newPlanToAdd === 'SEO_2M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>2</div>
                                    <div className="text-sm text-gray-600">publicaciones/mes</div>
                                    {newPlanToAdd === 'SEO_2M' && (
                                      <div className="mt-2 text-xs text-[#44aaf6] font-semibold">✓ Seleccionado</div>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setNewPlanToAdd('SEO_4M')}
                                    className={`p-4 rounded-xl border-2 transition-all ${
                                      newPlanToAdd === 'SEO_4M'
                                        ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <div className={`text-xl font-bold mb-1 ${newPlanToAdd === 'SEO_4M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>4</div>
                                    <div className="text-sm text-gray-600">publicaciones/mes</div>
                                    {newPlanToAdd === 'SEO_4M' && (
                                      <div className="mt-2 text-xs text-[#44aaf6] font-semibold">✓ Seleccionado</div>
                                    )}
                                  </button>
                                </div>
                                
                                {/* Campos específicos para SEO */}
                                {newPlanToAdd.startsWith('SEO') && (
                                  <div className="space-y-4 mt-4 p-4 bg-white rounded-xl border border-gray-200">
                                    <h6 className="font-semibold text-gray-700 text-center">Configuración SEO</h6>
                                    
                                    {/* Tipo de contenido SEO */}
                                    <div className="space-y-2">
                                      <label className="block text-sm font-medium text-gray-700">Tipo de contenido SEO</label>
                                      <div className="flex gap-4 justify-center">
                                        <label className="flex items-center gap-2 cursor-pointer select-none">
                                          <input
                                            type="radio"
                                            name="seoType"
                                            value="BLOG"
                                            checked={newPlanFields.seoType === 'BLOG'}
                                            onChange={(e) => setNewPlanFields(f => ({ ...f, seoType: e.target.value }))}
                                            className="hidden peer"
                                          />
                                          <span className="w-5 h-5 flex items-center justify-center rounded-full border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                            {newPlanFields.seoType === 'BLOG' && (
                                              <div className="w-2 h-2 bg-[#44aaf6] rounded-full"></div>
                                            )}
                                          </span>
                                          <span className="text-sm text-black font-medium">Blog</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer select-none">
                                          <input
                                            type="radio"
                                            name="seoType"
                                            value="BLOQUE_TEXTO"
                                            checked={newPlanFields.seoType === 'BLOQUE_TEXTO'}
                                            onChange={(e) => setNewPlanFields(f => ({ ...f, seoType: e.target.value }))}
                                            className="hidden peer"
                                          />
                                          <span className="w-5 h-5 flex items-center justify-center rounded-full border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                            {newPlanFields.seoType === 'BLOQUE_TEXTO' && (
                                              <div className="w-2 h-2 bg-[#44aaf6] rounded-full"></div>
                                            )}
                                          </span>
                                          <span className="text-sm text-black font-medium">Bloque de texto</span>
                                        </label>
                                      </div>
                                    </div>
                                    
                                    {/* Project ID para SEO */}
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">Project ID para SEO *</label>
                                      <input
                                        type="text"
                                        placeholder="Project ID específico para SEO"
                                        className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                                        value={newPlanFields.seoProjectId}
                                        onChange={(e) => setNewPlanFields(f => ({ ...f, seoProjectId: e.target.value }))}
                                        maxLength={100}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : user.plan?.startsWith('SEO') ? (
                              // Si tiene SEO, mostrar opciones de RRSS
                              <div className="space-y-4">
                                <h5 className="font-medium text-gray-600 text-center">Planes RRSS disponibles:</h5>
                                <div className="grid grid-cols-3 gap-3">
                                  <button
                                    type="button"
                                    onClick={() => setNewPlanToAdd('RRSS_4M')}
                                    className={`p-4 rounded-xl border-2 transition-all ${
                                      newPlanToAdd === 'RRSS_4M'
                                        ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <div className={`text-xl font-bold mb-1 ${newPlanToAdd === 'RRSS_4M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>4</div>
                                    <div className="text-sm text-gray-600">publicaciones/mes</div>
                                    {newPlanToAdd === 'RRSS_4M' && (
                                      <div className="mt-2 text-xs text-[#44aaf6] font-semibold">✓ Seleccionado</div>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setNewPlanToAdd('RRSS_6M')}
                                    className={`p-4 rounded-xl border-2 transition-all ${
                                      newPlanToAdd === 'RRSS_6M'
                                        ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <div className={`text-xl font-bold mb-1 ${newPlanToAdd === 'RRSS_6M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>6</div>
                                    <div className="text-sm text-gray-600">publicaciones/mes</div>
                                    {newPlanToAdd === 'RRSS_6M' && (
                                      <div className="mt-2 text-xs text-[#44aaf6] font-semibold">✓ Seleccionado</div>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setNewPlanToAdd('RRSS_12M')}
                                    className={`p-4 rounded-xl border-2 transition-all ${
                                      newPlanToAdd === 'RRSS_12M'
                                        ? 'border-[#44aaf6] bg-blue-50 shadow-lg'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <div className={`text-xl font-bold mb-1 ${newPlanToAdd === 'RRSS_12M' ? 'text-[#44aaf6]' : 'text-gray-700'}`}>12</div>
                                    <div className="text-sm text-gray-600">publicaciones/mes</div>
                                    {newPlanToAdd === 'RRSS_12M' && (
                                      <div className="mt-2 text-xs text-[#44aaf6] font-semibold">✓ Seleccionado</div>
                                    )}
                                  </button>
                                </div>
                                
                                {/* Campos específicos para RRSS */}
                                {newPlanToAdd.startsWith('RRSS') && (
                                  <div className="space-y-4 mt-4 p-4 bg-white rounded-xl border border-gray-200">
                                    <h6 className="font-semibold text-gray-700 text-center">Configuración RRSS</h6>
                                    
                                    {/* Project ID para RRSS */}
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">Project ID para RRSS *</label>
                                      <input
                                        type="text"
                                        placeholder="Project ID específico para RRSS"
                                        className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                                        value={newPlanFields.rrssProjectId}
                                        onChange={(e) => setNewPlanFields(f => ({ ...f, rrssProjectId: e.target.value }))}
                                        maxLength={100}
                                      />
                                    </div>
                                    
                                    {/* IDs de redes sociales */}
                                    <div className="space-y-3">
                                      <label className="block text-sm font-medium text-gray-700">IDs de redes sociales *</label>
                                      <div className="space-y-3">
                                        <div>
                                          <label className="block text-sm text-gray-600 mb-1">Instagram ID</label>
                                          <input
                                            type="text"
                                            placeholder="Instagram ID (opcional)"
                                            className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                                            value={newPlanFields.rrssInstagramId}
                                            onChange={(e) => setNewPlanFields(f => ({ ...f, rrssInstagramId: e.target.value }))}
                                            maxLength={100}
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-sm text-gray-600 mb-1">Facebook ID</label>
                                          <input
                                            type="text"
                                            placeholder="Facebook ID (opcional)"
                                            className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                                            value={newPlanFields.rrssFacebookId}
                                            onChange={(e) => setNewPlanFields(f => ({ ...f, rrssFacebookId: e.target.value }))}
                                            maxLength={100}
                                          />
                                        </div>
                                      </div>
                                      <p className="text-xs text-gray-500">* Al menos uno de los dos IDs es obligatorio</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : null}

                            {/* Mensaje de error */}
                            {addPlanError && (
                              <div className="text-red-500 text-center text-sm font-semibold mt-3">
                                {addPlanError}
                              </div>
                            )}

                            {/* Botón Confirmar */}
                            <div className="mt-4 text-center">
                              <button
                                type="button"
                                onClick={() => handleAddPlan(user)}
                                className="px-6 py-3 rounded-full bg-[#44aaf6] text-white font-bold text-lg shadow hover:bg-blue-600 transition-all disabled:opacity-60"
                                disabled={addPlanLoading || !newPlanToAdd}
                              >
                                {addPlanLoading ? 'Añadiendo...' : 'Confirmar'}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Selector tipo de imágenes (IA o propias) — SIEMPRE visible */}
                        <div className="mt-4 text-center">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ¿Qué tipo de imágenes quieres usar?
                          </label>
                          <div className="flex gap-4 justify-center">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={!editForm.useOwnPhotos}
                                onChange={() => setEditForm(f => ({ ...f, useOwnPhotos: false }))}
                                className="hidden peer"
                              />
                              <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                {!editForm.useOwnPhotos && (
                                  <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                              <span className="text-base text-black font-medium">IA</span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={editForm.useOwnPhotos}
                                onChange={() => setEditForm(f => ({ ...f, useOwnPhotos: true }))}
                                className="hidden peer"
                              />
                              <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                {editForm.useOwnPhotos && (
                                  <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                              <span className="text-base text-black font-medium">Propias</span>
                            </label>
                          </div>
                        </div>

                        {/* Selector de autoposting — SIEMPRE visible */}
                        <div className="mt-4 text-center">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Creación de publicaciones automática
                          </label>
                          <div className="flex gap-4 justify-center">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="radio"
                                name="userAutoposting"
                                checked={editForm.autoposting === true}
                                onChange={() => setEditForm(f => ({ ...f, autoposting: true }))}
                                className="hidden peer"
                              />
                              <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                {editForm.autoposting === true && (
                                  <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                              <span className="text-base text-black font-medium">Activada</span>
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="radio"
                                name="userAutoposting"
                                checked={editForm.autoposting === false}
                                onChange={() => setEditForm(f => ({ ...f, autoposting: false }))}
                                className="hidden peer"
                              />
                              <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                {editForm.autoposting === false && (
                                  <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                              <span className="text-base text-black font-medium">Desactivada</span>
                            </label>
                          </div>
                        </div>

                        {/* Selector de confirmación de posts — Solo si plan incluye RRSS */}
                        {(editForm.plan.startsWith('RRSS') || editForm.plan === 'BOTH') && (
                          <div className="mt-4 text-center">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Confirmación de posts
                            </label>
                            <div className="flex gap-4 justify-center">
                              <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                  type="radio"
                                  name="userConfirmation"
                                  checked={confirmationEnabled === true}
                                  onChange={() => {
                                    setConfirmationEnabled(true);
                                    if (!editForm.confirmationMailAddress) {
                                      setEditForm(f => ({ ...f, confirmationMailAddress: '', confirmationPhone: '' }));
                                    }
                                  }}
                                  className="hidden peer"
                                />
                                <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                  {confirmationEnabled === true && (
                                    <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </span>
                                <span className="text-base text-black font-medium">Activada</span>
                              </label>

                              <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                  type="radio"
                                  name="userConfirmation"
                                  checked={confirmationEnabled === false}
                                  onChange={() => {
                                    setConfirmationEnabled(false);
                                    setEditForm(f => ({ ...f, confirmationMailAddress: '', confirmationPhone: '' }));
                                  }}
                                  className="hidden peer"
                                />
                                <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                  {confirmationEnabled === false && (
                                    <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </span>
                                <span className="text-base text-black font-medium">Desactivada</span>
                              </label>
                            </div>
                            
                            {/* Campos de confirmación — Solo visible si está activada */}
                            {confirmationEnabled && (
                              <div className="mt-4 flex flex-col gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email de confirmación
                                  </label>
                                  <input
                                    type="email"
                                    placeholder="Email para confirmaciones"
                                    className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full max-w-md mx-auto"
                                    value={editForm.confirmationMailAddress || ''}
                                    onChange={e => setEditForm(f => ({ ...f, confirmationMailAddress: e.target.value }))}
                                    maxLength={100}
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirmación por WhatsApp
                                  </label>
                                  <div className="flex items-center rounded-xl border border-gray-300 bg-white focus-within:border-[#44aaf6] overflow-hidden max-w-md mx-auto">
                                    <select
                                      value={editConfirmationPhonePrefix}
                                      onChange={e => {
                                        setEditConfirmationPhonePrefix(e.target.value);
                                      }}
                                      className="appearance-none bg-transparent px-3 py-2 text-lg focus:outline-none cursor-pointer min-w-[60px] text-gray-700"
                                      style={{border: 'none', outline: 'none'}}>
                                      {countryPrefixes.map(country => (
                                        <option key={country.prefix} value={country.prefix}>{country.prefix}</option>
                                      ))}
                                    </select>
                                    <span className="text-gray-300 select-none" style={{fontWeight: 'bold'}}>|</span>
                                    <input
                                      type="tel"
                                      placeholder="611 48 07 09"
                                      className="flex-1 px-4 py-2 text-lg bg-transparent focus:outline-none border-none"
                                      value={editForm.confirmationPhone || ''}
                                      onChange={e => {
                                        const value = e.target.value;
                                        const cleaned = value.replace(/[^\d\s\-]/g, '');
                                        setEditForm(f => ({ ...f, confirmationPhone: cleaned }));
                                      }}
                                      maxLength={15}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Selector tipo de SEO — Solo si plan incluye SEO */}
                        {(editForm.plan.startsWith('SEO') || editForm.plan === 'BOTH') && (
                          <div className="space-y-3 mt-4 text-center">
                            <label className="block text-sm text-gray-700 mb-2">
                              Tipo de contenido SEO 
                            </label>
                            <div className="flex gap-6 justify-center">
                              <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={editForm.seo.includes('BLOG')}
                                  onChange={() =>
                                    setEditForm((f) => ({
                                      ...f,
                                      seo: ['BLOG'],
                                    }))
                                  }
                                  className="hidden peer"
                                />
                                <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                  {editForm.seo.includes('BLOG') && (
                                    <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </span>
                                <span className="text-base text-black font-medium">Blog</span>
                              </label>

                              <label className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={editForm.seo.includes('BLOQUE_TEXTO')}
                                  onChange={() =>
                                    setEditForm((f) => ({
                                      ...f,
                                      seo: ['BLOQUE_TEXTO'],
                                    }))
                                  }
                                  className="hidden peer"
                                />
                                <span className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white">
                                  {editForm.seo.includes('BLOQUE_TEXTO') && (
                                    <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </span>
                                <span className="text-base text-black font-medium">Bloque de texto</span>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Línea 4: Teléfono (con prefijo) y Project ID */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                         
                          <div className="flex-1 min-w-[180px] max-w-[320px]">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Project ID</label>
                            <input
                              type="text"
                              placeholder="Project ID"
                              className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                              value={editForm.projectId}
                              onChange={e => setEditForm((f: EditForm) => ({ ...f, projectId: e.target.value }))}
                              maxLength={100}
                            />
                          </div>
                        </div>
                        {/* Línea 5: Facebook ID e Instagram ID (solo si RRSS) */}
                        {editForm.plan.startsWith('RRSS') && (
                          <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1 min-w-[180px] max-w-[320px]">
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Facebook ID</label>
                              <input
                                type="text"
                                placeholder="Facebook ID"
                                className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                                value={editForm.facebookId}
                                onChange={e => setEditForm((f: EditForm) => ({ ...f, facebookId: e.target.value }))}
                                maxLength={100}
                              />
                            </div>
                            <div className="flex-1 min-w-[180px] max-w-[320px]">
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Instagram ID</label>
                              <input
                                type="text"
                                placeholder="Instagram ID"
                                className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full"
                                value={editForm.instagramId}
                                onChange={e => setEditForm((f: EditForm) => ({ ...f, instagramId: e.target.value }))}
                                maxLength={100}
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {/* Línea 6: Botones y error */}
                    {editError && <div className="text-red-500 text-center text-sm font-semibold">{editError}</div>}
                    <div className="flex gap-4 justify-end mt-2">
                      <button
                        type="button"
                        className="px-8 py-3 rounded-full border border-gray-300 text-gray-700 font-bold shadow hover:bg-gray-50 transition-all text-lg"
                        onClick={handleCancelEdit}
                        disabled={editLoading}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-8 py-3 rounded-full bg-[#44aaf6] text-white font-bold text-lg shadow hover:bg-blue-600 transition-all disabled:opacity-60"
                        disabled={editLoading}
                      >
                        {editLoading ? 'Guardando...' : 'Guardar'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Desplegable de envío de correo */}
              {emailingUserId === user.id && (
                <div className="w-full flex justify-center">
                  <form
                    className="w-full max-w-2xl flex flex-col gap-4 p-4 sm:p-6 md:p-8 animate-fade-in"
                    style={{ zIndex: 50, position: 'relative' }}
                    onSubmit={handleSendEmailSubmit}
                    onClick={e => e.stopPropagation()}
                  >
                    {/* Título del apartado */}
                    <h2 className="text-3xl font-bold text-[#44aaf6] mb-0 text-center">Enviar credenciales al mail</h2>
                    {/* Subtítulo explicativo */}
                    <p className="text-sm text-black mb-1 text-center" style={{opacity: 0.85}}>
                      Se enviará un correo con las credenciales de <span className="font-semibold">{user.email}</span> a la dirección que especifiques aquí. <span className="text-[#44aaf6] font-semibold">La dirección que escribas no se guardará</span> en ningún lado.
                    </p>
                    {/* Campo oculto para prevenir envío múltiple */}
                    <input type="hidden" name="submitted" value={emailLoading ? 'true' : 'false'} />
                  

                                            {/* Campo Email */}
                        <div className="relative">
                          <input
                            type="email"
                            required
                            placeholder="Email donde enviar las credenciales"
                            className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full pr-10 disabled:opacity-50 disabled:cursor-not-allowed"
                            value={emailDestination}
                            onChange={(e) => setEmailDestination(e.target.value)}
                            disabled={emailLoading}
                          />
                      {emailDestination.length > 0 && !emailLoading && (
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#44aaf6] text-xl"
                          onClick={() => setEmailDestination('')}
                          tabIndex={0}
                          aria-label="Limpiar email"
                        >
                          ×
                        </button>
                      )}
                    </div>

                                            {/* Campo Nueva Contraseña */}
                        <div className="relative">
                          <input
                            type={showEmailPassword ? "text" : "password"}
                            required
                            placeholder="Nueva contraseña (obligatorio)"
                            className="rounded-xl border border-gray-300 px-4 py-2 text-lg focus:border-[#44aaf6] focus:outline-none w-full pr-20 disabled:opacity-50 disabled:cursor-not-allowed"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={emailLoading}
                          />
                      {newPassword.length > 0 && !emailLoading && (
                        <>
                          <button
                            type="button"
                            className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#44aaf6] transition-colors"
                            onClick={() => setShowEmailPassword(!showEmailPassword)}
                            tabIndex={0}
                            aria-label={showEmailPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                          >
                            <EyeIcon open={showEmailPassword} />
                          </button>
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#44aaf6] text-xl"
                            onClick={() => setNewPassword('')}
                            tabIndex={0}
                            aria-label="Limpiar contraseña"
                          >
                            ×
                          </button>
                        </>
                      )}
                    </div>

                    {/* Botones */}
                    <div className="flex gap-4 justify-end mt-4">
                      <button
                        type="button"
                        className="px-8 py-3 rounded-full border border-gray-300 text-gray-700 font-bold shadow hover:bg-gray-50 transition-all text-lg"
                        onClick={handleCancelEmail}
                        disabled={emailLoading}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="px-8 py-3 rounded-full bg-[#44aaf6] text-white font-bold text-lg shadow hover:bg-blue-600 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                        disabled={emailLoading || !emailDestination.trim() || !newPassword.trim()}
                      >
                        {emailLoading ? (
                          <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Enviar
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
              ))}
          {/* Paginación */}
          {total > 0 && (
            <div className="flex justify-end items-center mt-6">
              <span className="text-gray-400 text-lg mr-6 select-none">{startIdx + 1}-{endIdx} de {total}</span>
              <button
                className={`mx-1 text-2xl transition ${page === 1 ? 'text-gray-300' : 'text-[#44aaf6] hover:text-blue-600'}`}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label="Anterior"
              >
                &#8592;
              </button>
              <button
                className={`mx-1 text-2xl transition ${page === totalPages ? 'text-gray-300' : 'text-[#44aaf6] hover:text-blue-600'}`}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                aria-label="Siguiente"
              >
                &#8594;
              </button>
            </div>
          )}
        </div>
      )}



    </div>
  );
};

// Componente de calendario simple
function CalendarPopup({ onSelect, selectedDate }: { onSelect: (date: string) => void, selectedDate: string | null }) {
  const today = new Date();
  // Inicializa el mes y año según selectedDate o el día actual
  const initial = selectedDate ? new Date(selectedDate) : today;
  const [year, setYear] = useState(initial.getFullYear());
  const [month, setMonth] = useState(initial.getMonth());

  // Cuando selectedDate cambie (al abrir el calendario), sincroniza mes y año
  useEffect(() => {
    const base = selectedDate ? new Date(selectedDate) : today;
    setYear(base.getFullYear());
    setMonth(base.getMonth());
  }, [selectedDate]);

  const day = today.getDate();
  const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const weekDays = ['LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB', 'DOM'];

  // Generar días del mes
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  let startDay = firstDay.getDay();
  if (startDay === 0) startDay = 7; // Lunes=1, Domingo=7
  const prevMonthDays = [];
  for (let i = startDay - 1; i > 0; i--) {
    prevMonthDays.push('');
  }
  const days = [
    ...prevMonthDays,
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1)
  ];
  while (days.length % 7 !== 0) days.push('');

  // Cambiar de mes
  const handlePrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(y => y - 1);
    } else {
      setMonth(m => m - 1);
    }
  };
  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(y => y + 1);
    } else {
      setMonth(m => m + 1);
    }
  };

  // Formato YYYY-MM-DD para comparar
  function formatDate(y: number, m: number, d: number) {
    return `${y}-${(m + 1).toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`;
  }

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center justify-between w-full mb-2">
        <button className="text-2xl text-gray-400 px-2" onClick={handlePrevMonth}>{'<'}</button>
        <span className="font-semibold text-lg text-gray-700">{monthNames[month]} {year}</span>
        <button className="text-2xl text-gray-400 px-2" onClick={handleNextMonth}>{'>'}</button>
      </div>
      <div className="grid grid-cols-7 gap-1 w-full mb-1">
        {weekDays.map((d, i) => (
          <div key={i} className="text-center text-xs font-semibold text-gray-400 pb-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 w-full">
        {days.map((d, i) => {
          const isToday = d === day && year === today.getFullYear() && month === today.getMonth();
          const isSelected = selectedDate && typeof d === 'number' && formatDate(year, month, d) === selectedDate;
          return (
            <div
              key={i}
              className={`flex items-center justify-center aspect-square rounded-full select-none text-base font-medium transition-all cursor-pointer
                ${isSelected ? 'bg-[#e6f2fd] text-[#44aaf6] border-2 border-[#44aaf6] font-bold' :
                  isToday ? 'border-2 border-[#44aaf6] text-[#44aaf6] bg-white' :
                  d ? 'text-gray-700 bg-white hover:bg-blue-50' : 'text-gray-300 bg-white'}
              `}
              style={{ minHeight: 32 }}
              onClick={() => typeof d === 'number' && onSelect(formatDate(year, month, d))}
            >
              {d || ''}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Icono de ojo igual que en App.tsx
const EyeIcon = ({ open }: { open: boolean }) => (
  open ? (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.956 9.956 0 012.293-3.95m3.249-2.383A9.956 9.956 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.973 9.973 0 01-4.293 5.03M15 12a3 3 0 11-6 0 3 3 0 016 0zm-6.364 6.364L19.07 4.93" /></svg>
  )
);

export default AdminUserListScreen; 